import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch, useFieldArray } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronDown, ChevronRight, ChevronsUpDown, Plus, Tag, History as HistoryIcon, CheckSquare, Trash2, EyeOff, FolderInput, X, Power, FileSpreadsheet, Download } from "lucide-react";
import { AuditHistoryDialog } from "./audit-history-dialog";
import { ImportItemsDialog } from "./import-items-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { checkHsnGstMatch, HSN_GST_ADVISORY } from "@workspace/billing";
import {
  useListInventoryItems,
  useCreateInventoryItem,
  useUpdateInventoryItem,
  useListInventoryCategories,
  useListInventoryPlans,
  useListPlanEntitlements,
  getListPlanEntitlementsQueryKey,
  useListItemVariants,
  useCreateAttributeValue,
  useCreateItemVariant,
  useDeleteItemVariant,
  useListAttributeKeys,
  useDeleteInventoryItem,
  useListItemRecipes,
  getListInventoryItemsQueryKey,
  getListItemVariantsQueryKey,
  ChargingModel,
  StockFrequency,
  QuotaPeriod,
} from "@workspace/api-client-react";
import type { InventoryItem, InventoryItemVariant } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { ListPagination } from "@/components/common/list-pagination";
import { cn } from "@/lib/utils";
import { itmId } from "@/lib/display-id";
import { specsToLine } from "@/lib/specs";
import { StatusWithAlertDot } from "@/components/inventory/status-with-alert-dot";
import { ItemsPriceHistoryRow } from "@/components/inventory/items-price-history-row";
import { VariantDialog } from "@/components/inventory/variant-dialog";
import { RecipeBuilderSection } from "@/components/inventory/recipe-builder-section";
import { ModifierListSection } from "@/components/inventory/modifier-list-section";
import { RecipePill } from "@/components/inventory/recipe-pill";
import { CostDriftPill } from "@/components/inventory/cost-drift-pill";
import { CreateVariantRow } from "@/components/inventory/create-variant-row";
import { ItemPhotosInput } from "@/components/inventory/item-photos-input";
import { ItemSpecsInput, type SpecPair } from "@/components/inventory/item-specs-input";
import { ParentItemCombobox } from "@/components/inventory/parent-item-combobox";
import { HsnCodeCombobox } from "@/components/inventory/hsn-code-combobox";
import { CreateRecipeRow } from "@/components/inventory/create-recipe-row";
import {
  PreparationStepsInput,
  PreparationStepsSection,
} from "@/components/inventory/preparation-steps-input";

// Searchable category picker — popover + cmdk filter; ~5 rows visible, scrolls past that.
// Keeps the existing `CAT-NNN — Name | Model (Recipe)` display format.
// Toolbar filter — caps initial render to 5 categories. Operator searches via
// CommandInput to find the rest (cmdk does its own filter internally). "Show all
// N" item below the 5 reveals the full list. Distinct from CategoryCombobox
// (used inside the item form) — that one ranks by relevance and always shows all.
function CategoryFilterCombobox({
  value,
  categories,
  onChange,
}: {
  value: number | undefined;
  categories: Array<{ id: number; name: string; chargingModel: string }>;
  onChange: (id: number | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const selected = categories.find((c) => c.id === value);
  const triggerLabel = selected
    ? `${selected.name} | ${selected.chargingModel.replace(/_/g, " ")}`
    : "All categories";

  const isSearching = search.trim().length > 0;
  const visible = isSearching || showAll ? categories : categories.slice(0, 5);
  const overflow = categories.length - visible.length;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setSearch("");
          setShowAll(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[220px] justify-between font-normal"
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search categories…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>No category found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__ All categories"
                onSelect={() => { onChange(undefined); setOpen(false); }}
                className={cn(value === undefined && "bg-primary/10 text-primary font-medium aria-selected:bg-primary/15")}
              >
                <Check className={cn("mr-2 h-4 w-4", value === undefined ? "opacity-100" : "opacity-0")} />
                <span>All categories</span>
              </CommandItem>
              {visible.map((cat) => {
                const label = `${cat.name} | ${cat.chargingModel.replace(/_/g, " ")}`;
                const isSelected = value === cat.id;
                return (
                  <CommandItem
                    key={cat.id}
                    value={label}
                    onSelect={() => { onChange(cat.id); setOpen(false); }}
                    className={cn(isSelected && "bg-primary/10 text-primary font-medium aria-selected:bg-primary/15")}
                  >
                    <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{label}</span>
                  </CommandItem>
                );
              })}
              {!isSearching && !showAll && overflow > 0 && (
                <CommandItem
                  value="__show_all__"
                  onSelect={() => setShowAll(true)}
                  className="text-xs text-muted-foreground justify-center"
                >
                  Show all {categories.length} categories
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CategoryCombobox({
  value,
  categories,
  onChange,
}: {
  value: number | null | undefined;
  categories: Array<{ id: number; name: string; chargingModel: string }>;
  onChange: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = categories.find((c) => c.id === value);
  const selectedLabel = selected
    ? `${selected.name} | ${selected.chargingModel.replace(/_/g, " ")}`
    : "Select category";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground")}
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search category…" />
          <CommandList className="max-h-[220px]">
            <CommandEmpty>No category found.</CommandEmpty>
            <CommandGroup>
              {categories.map((cat) => {
                const label = `${cat.name} | ${cat.chargingModel.replace(/_/g, " ")}`;
                const isSelected = value === cat.id;
                return (
                  <CommandItem
                    key={cat.id}
                    value={label}
                    onSelect={() => { onChange(cat.id); setOpen(false); }}
                    className={cn(isSelected && "bg-primary/10 text-primary font-medium aria-selected:bg-primary/15")}
                  >
                    <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// 2026-05-21: BrandCombobox removed. Brand is no longer a top-level Identification
// field — it's entered as a normal "Brand" specification row (ItemSpecsInput) and
// synced back to inventory_items.brand on save.

const CHARGING_MODEL_COLORS: Record<string, string> = {
  Complimentary_Unlimited: "bg-green-100 text-green-700",
  Complimentary_With_Limit: "bg-teal-100 text-teal-700",
  Pay_Per_Use: "bg-orange-100 text-orange-700",
  Plan_Inclusive_Quota: "bg-blue-100 text-blue-700",
  AddOn_Optional: "bg-purple-100 text-purple-700",
  Internal_Use: "bg-slate-100 text-slate-700",
};

const MEASUREMENT_UNITS = ["gram", "kg", "ml", "litre"] as const;

// 2026-05-21: items-list stock display. Strip trailing ".00" from the numeric()
// string and render "piece" as the friendlier "unit"/"units" (operator request).
// Real measurement units (gram/kg/ml/box/…) are shown verbatim.
function formatStock(qty: number | string | null | undefined, unit: string): string {
  const n = Number(qty) || 0;
  // String(n) already drops trailing ".00" (0.00 → "0", 1.50 → "1.5").
  const u = unit === "piece" ? (n === 1 ? "unit" : "units") : unit;
  return `${String(n)} ${u}`;
}

// specsToLine moved to @/lib/specs (shared with the stock-in item picker).

const ItemFormSchema = z.object({
  // Section A — Identification
  name: z.string().min(1, "Item name is required"),
  itemCode: z.string().nullable().optional(),
  // 2026-05-21: Brand is no longer a top-level Identification field — it's managed
  // as a normal "Brand" specification row and synced back to this column on save.
  brand: z.string().nullable().optional(),
  // 2026-05-18: Product (physical goods) vs Service (intangible — print, scan, etc.).
  // Orthogonal to isRecipe; defaults to Product.
  itemKind: z.enum(["Product", "Service"]).default("Product"),
  // 2026-05-20: basis for stock-out / refill measurement (Piece / Pack / Weight / Volume),
  // derived from the item's measure-type specs. stockOutUnit holds the gram/kg or ml/litre
  // when the basis is Weight or Volume.
  // 2026-05-21: when set, this item is a variant of the given parent item.
  parentItemId: z.number().nullable().optional(),
  // Free-form: "Item" (always) or any spec attribute label except descriptive ones.
  stockOutBasis: z.string().nullable().optional(),
  stockOutUnit: z.enum(["gram", "kilogram", "ml", "litre"]).nullable().optional(),
  stockOutQty: z.number().nullable().optional(),
  categoryId: z.number().min(1, "Category is required"),
  // brandAgnosticSpecs removed from the form (hot-fix 2026-05-14). DB column stays NOT NULL;
  // the onSubmit handler sends "-" as a placeholder so the server contract is satisfied.
  brandAgnosticSpecs: z.string().optional().default("-"),
  hsnSacCode: z.string().min(1, "HSN/SAC code is required"),
  unitOfMeasurement: z.enum(["piece", "box", "gram", "kg", "ml", "litre", "ream", "pack"]),
  packSize: z.number().nullable().optional(),
  // Phase 7: variants inline at create time. Local-only `_qty` / `_unit` carry
  // the compound input state for Volume/Weight/Piece keys; never sent to the API.
  variants: z.array(z.object({
    attributeKeyId: z.number().nullable().optional(),
    attributeValueId: z.number().nullable().optional(),
    attributeValueText: z.string().optional(),
    itemCode: z.string().nullable().optional(),
    displayName: z.string().nullable().optional(),
    currentStock: z.number().default(0),
    // 2026-05-18: per-variant price override. NULL = inherit parent item.sellingPrice.
    sellingPrice: z.number().nullable().optional(),
    status: z.enum(["Active", "Discontinued"]).default("Active"),
    _qty: z.number().nullable().optional(),
    _unit: z.string().optional(),
  })).optional().default([]),
  // Phase 7: MRP
  mrp: z.number().nullable().optional(),
  // Section B — Usage Model (formerly "Charging Model"; renamed in UI 2026-05-18 to fit
  // both client-facing and internal items. The DB column + enum slug stays `chargingModel`.)
  chargingModel: z.enum([
    "Complimentary_Unlimited",
    "Complimentary_With_Limit",
    "Pay_Per_Use",
    "Plan_Inclusive_Quota",
    "AddOn_Optional",
    "Internal_Use",
  ]),
  // 2026-05-19: 1-3 photo URLs per item. Required on Create (enforced in superRefine below).
  photoUrls: z.array(z.string()).max(3, "Maximum 3 photos allowed").default([]),
  // 2026-05-19: flat key-value specs for a single SKU (Brand / Weight / Flavour / Composition / etc.).
  // Distinct from variants — those are true multi-SKU products with separate stock/prices.
  // Min-1 NOT enforced here so half-filled rows don't block Save; onSubmit filters blank
  // rows out before sending to the API.
  specs: z.array(z.object({ label: z.string(), value: z.string() })).default([]),
  // Phase 10 (Plan 10-07): freeQuantity / periodType / priceAfterLimit removed — see planQuotas[].
  sellingPrice: z.number().nullable().optional(),
  memberDiscountPrice: z.number().nullable().optional(),
  gstRateOnSale: z.number().nullable().optional(),
  paymentCollection: z.enum(["immediate", "monthly_invoice"]).nullable().optional(),
  requiresPosEntry: z.boolean().nullable().optional(),
  overageRate: z.number().nullable().optional(),
  // Phase 7: Is Recipe toggle (gated by category.allowsRecipe)
  isRecipe: z.boolean().default(false),
  recipeComponents: z.array(z.object({
    componentItemId: z.number().min(1),
    qtyPerServe: z.number().min(0.001),
    // 2026-05-18: 'tracked' (default — decrement stock on serve) | 'estimated'
    // (display-only dependency, no auto-decrement). For e.g. ink usage per print.
    consumptionMode: z.enum(["tracked", "estimated"]).default("tracked"),
    notes: z.string().nullable().optional(),
  })).optional().default([]),
  // Phase 55 (RECIPE-SPEC-01, migration 0121): create-mode ordered preparation
  // steps. Only submitted (server-side) when Is Recipe is on. Edit-mode uses a
  // separate self-contained PreparationStepsSection that PATCHes directly.
  preparationSteps: z.array(z.string().max(500)).default([]),
  // Section C — Storage & Handling
  inventoryTracked: z.boolean().nullable().optional(),
  // 2026-05-18 operator request: editable initial stock on Create; read-only on Edit.
  currentStock: z.number().nullable().optional(),
  storageLocation: z.enum(["Pantry", "Store_Room", "IT_Rack", "Cabin_Store"]).nullable().optional(),
  shelfLifeDays: z.number().nullable().optional(),
  batchTracked: z.boolean().nullable().optional(),
  // Section D — Stock Frequency (D-17)
  stockInFrequency: z.enum(["Daily", "Weekly", "Monthly", "On_Demand"]),
  stockOutFrequency: z.enum(["Daily", "Weekly", "Monthly", "On_Demand"]),
  reorderLevel: z.number().nullable().optional(),
  reorderQuantity: z.number().nullable().optional(),
  safetyStock: z.number().nullable().optional(),
  maxStockLevel: z.number().nullable().optional(),
  orderFrequencyDays: z.number().nullable().optional(),
  standardOrderQty: z.number().nullable().optional(),
  dailyQuantity: z.number().nullable().optional(),
  // Section E — Status
  status: z.enum(["Active", "Discontinued", "Seasonal"]).optional(),
  notes: z.string().nullable().optional(),
  // Phase 10 — per-plan quotas for Complimentary_With_Limit items. Each row creates a
  // plan_entitlements row in the same atomic transaction. `planName` is display-only and
  // is stripped from the POST/PATCH payload in onSubmit.
  planQuotas: z.array(z.object({
    planId: z.number().int(),
    planName: z.string(),
    quotaQuantity: z.number(),
    quotaPeriod: z.enum([
      "per_day",
      "per_month",
      "per_lifetime",
      "per_membership",
      "per_visit",
      "per_booking",
      "per_hour",
    ]),
    overageRate: z.number().nullable(),
  })).optional().default([]),
}).superRefine((data, ctx) => {
  // Phase 10 CWL-01 mirror: CWL items must have at least 1 plan quota row.
  if (data.chargingModel === "Complimentary_With_Limit" && (!data.planQuotas || data.planQuotas.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["planQuotas"],
      message: "At least one plan quota row is required for Complimentary With Limit items",
    });
  }

  // 2026-05-18 operator request: MRP is mandatory for ALL items.
  if (data.mrp == null || data.mrp <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["mrp"],
      message: "MRP is required (must be > 0)",
    });
  }

  // 2026-05-18 operator request (revised): item-level sellingPrice is only required
  // for Pay_Per_Use. CWL captures overage rates per-plan inside the planQuotas matrix.
  if (data.chargingModel === "Pay_Per_Use" && (data.sellingPrice == null || data.sellingPrice <= 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sellingPrice"],
      message: "Selling Price is required (must be > 0)",
    });
  }

  // For CWL: every planQuotas row needs an overageRate > 0 (operator framing —
  // matrix is the source of truth; can't ship CWL items with zero overage rates).
  if (data.chargingModel === "Complimentary_With_Limit" && data.planQuotas?.length) {
    data.planQuotas.forEach((row, i) => {
      if (row.overageRate == null || row.overageRate <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["planQuotas", i, "overageRate"],
          message: "Overage Rate is required (must be > 0)",
        });
      }
    });
  }
});

type ItemFormValues = z.infer<typeof ItemFormSchema>;

// 2026-07-24 hot-fix: legacy items carry unit_of_measurement values that predate the
// locked-down enum below — chiefly "Item" (61 rows) and "Piece" (18 rows). Seeding the
// form with a value the zod enum rejects makes zodResolver fail validation on a field
// that ISN'T EVEN RENDERED, so form.handleSubmit() silently refuses to fire onSubmit —
// every Save (including an MRP change) becomes a no-op with no error surfaced. Normalise
// legacy/casing variants into the canonical enum so the form always seeds valid and the
// PATCH sends a server-accepted value (which then heals the row on save).
const UOM_ENUM = ["piece", "box", "gram", "kg", "ml", "litre", "ream", "pack"] as const;
function normalizeUom(raw: unknown): ItemFormValues["unitOfMeasurement"] {
  if (typeof raw !== "string" || !raw.trim()) return "piece";
  const v = raw.trim().toLowerCase();
  if ((UOM_ENUM as readonly string[]).includes(v)) return v as ItemFormValues["unitOfMeasurement"];
  // Legacy aliases → canonical enum member.
  if (["item", "items", "each", "unit", "units", "nos", "no", "pcs", "pc"].includes(v)) return "piece";
  if (["liter", "liters", "litres", "l"].includes(v)) return "litre";
  if (["millilitre", "milliliter", "mls"].includes(v)) return "ml";
  if (["kilogram", "kilograms", "kgs"].includes(v)) return "kg";
  if (["grams", "gm", "gms", "g"].includes(v)) return "gram";
  if (["boxes"].includes(v)) return "box";
  if (["reams"].includes(v)) return "ream";
  if (["packs", "packet", "packets"].includes(v)) return "pack";
  return "piece";
}

interface Props {
  openCreate: boolean;
  setOpenCreate: (open: boolean) => void;
}

function ItemDialog({
  open,
  onClose,
  editItem,
}: {
  open: boolean;
  onClose: () => void;
  editItem: InventoryItem | null;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Defer-mount the form body. The dialog has ~180 FormField children
  // that all mount in one synchronous React commit when `open` flips
  // true — long enough on the main thread that the operator perceives
  // a "hang" between clicking Add Item and the dialog appearing.
  // We paint the Dialog SHELL (header + skeleton + footer) immediately,
  // then flip `bodyReady` on the next animation frame so the heavy
  // form body mounts in a second commit. The click feels instant, the
  // skeleton fills the dialog for a couple of frames, then the real
  // form appears. Reset to false when the dialog closes so the next
  // open also gets the deferred path.
  const [bodyReady, setBodyReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setBodyReady(false);
      return;
    }
    let cancelled = false;
    // Two RAFs: the first lets the dialog shell + skeleton paint; the
    // second mounts the heavy body. Single RAF sometimes batches with
    // the commit and the skeleton never gets a chance to render.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        if (!cancelled) setBodyReady(true);
      });
      return raf2;
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Edit-mode variant management state
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [editVariant, setEditVariant] = useState<InventoryItemVariant | null>(null);
  const [deleteVariantTarget, setDeleteVariantTarget] = useState<InventoryItemVariant | null>(null);

  const isEditMode = editItem !== null;

  // ROOT CAUSE of the Add Item infinite-render bug:
  //
  //   Before the fix, this hook had TWO unstable-reference issues
  //   that interacted with @tanstack/react-query v5 to cause an
  //   unbounded re-render loop in create mode:
  //
  //   1. queryKey was a fresh array literal each render
  //      (`["listItemVariants", editItem?.id ?? 0, "dialog"]`).
  //      react-query v5 deduplicates by hashing the key, so different
  //      array REFERENCES with the same content shouldn't loop —
  //      but orval's generated hook merges this into a fresh options
  //      object every render, and useQuery's subscription path
  //      re-emits when options reference changes, kicking ItemDialog
  //      back into render.
  //
  //   2. The destructure `data: dialogVariants = []` produces a new
  //      empty array on every render whenever `data` is undefined
  //      (which it is when the query is disabled in create mode).
  //      Nothing in this file depends on that array's identity, but
  //      together with #1 the loop became self-sustaining at hundreds
  //      of renders/second.
  //
  // Fix:
  //   - useMemo the queryKey so its reference is stable per-editItem.
  //   - Pull out the raw data and useMemo the `?? []` fallback so
  //     the empty-array reference is stable too.
  const dialogVariantsQueryKey = useMemo(
    () => ["listItemVariants", editItem?.id ?? 0, "dialog"] as const,
    [editItem?.id],
  );
  const { data: dialogVariantsRaw } = useListItemVariants(
    editItem?.id ?? 0,
    undefined,
    {
      query: {
        queryKey: dialogVariantsQueryKey as unknown as readonly unknown[],
        enabled: isEditMode && open,
      },
    },
  );
  const dialogVariants = useMemo(
    () => dialogVariantsRaw ?? [],
    [dialogVariantsRaw],
  );

  const deleteVariantMutation = useDeleteItemVariant();
  const createVariantMutation = useCreateItemVariant();
  const createAttributeValueMutation = useCreateAttributeValue();
  const { data: attributeKeys } = useListAttributeKeys();
  // Composite-SKU hot-fix 2026-05-18: each variant row can carry one or more EXTRA
  // (key, value) pairs in addition to the primary row's attribute. On save, all are
  // resolved to attribute_value_ids and POSTed as attributeValueIds[].
  // Keyed by variant-row index; each entry is the array of extra pairs.
  const [extraAttrs, setExtraAttrs] = useState<Record<number, Array<{ attributeKeyId: number | null; attributeValueId: number | null; attributeValueText: string }>>>({});

  function addExtraAttr(rowIdx: number) {
    setExtraAttrs((prev) => ({
      ...prev,
      [rowIdx]: [...(prev[rowIdx] ?? []), { attributeKeyId: null, attributeValueId: null, attributeValueText: "" }],
    }));
  }
  function removeExtraAttr(rowIdx: number, extraIdx: number) {
    setExtraAttrs((prev) => ({
      ...prev,
      [rowIdx]: (prev[rowIdx] ?? []).filter((_, i) => i !== extraIdx),
    }));
  }
  function updateExtraAttr(rowIdx: number, extraIdx: number, patch: Partial<{ attributeKeyId: number | null; attributeValueId: number | null; attributeValueText: string }>) {
    setExtraAttrs((prev) => {
      const list = [...(prev[rowIdx] ?? [])];
      const target = list[extraIdx];
      if (!target) return prev;
      list[extraIdx] = { ...target, ...patch };
      return { ...prev, [rowIdx]: list };
    });
  }

  async function handleSavePendingVariant(idx: number) {
    if (!editItem) return;
    const row = form.getValues(`variants.${idx}`) as { attributeKeyId?: number | null; attributeValueId?: number | null; attributeValueText?: string; itemCode?: string | null; currentStock?: number; status?: "Active" | "Discontinued" };
    if (!row.attributeKeyId) {
      toast({ title: "Pick an attribute key first.", variant: "destructive" });
      return;
    }
    if (!row.attributeValueId && !row.attributeValueText) {
      toast({ title: "Enter a value.", variant: "destructive" });
      return;
    }
    try {
      // Resolve every (key, value) pair — primary + extras — into attribute_value_ids.
      // Create attribute_value rows on-the-fly for any free-text entries.
      const allPairs = [
        { attributeKeyId: row.attributeKeyId, attributeValueId: row.attributeValueId ?? null, attributeValueText: row.attributeValueText ?? "" },
        ...(extraAttrs[idx] ?? []).filter((p) => p.attributeKeyId != null && (p.attributeValueId != null || p.attributeValueText)),
      ];
      const attributeValueIds: number[] = [];
      for (const p of allPairs) {
        let valueId = p.attributeValueId ?? null;
        if (!valueId && p.attributeValueText) {
          const created = await createAttributeValueMutation.mutateAsync({
            data: { attributeKeyId: p.attributeKeyId!, value: p.attributeValueText },
          });
          valueId = (created as { id?: number })?.id ?? null;
        }
        if (valueId) attributeValueIds.push(valueId);
      }
      if (attributeValueIds.length === 0) {
        toast({ title: "No attribute values could be resolved.", variant: "destructive" });
        return;
      }
      await createVariantMutation.mutateAsync({
        itemId: editItem.id,
        data: {
          attributeValueIds,
          itemCode: row.itemCode ?? null,
          currentStock: row.currentStock != null ? String(row.currentStock) : null,
          status: (row.status ?? "Active") as "Active" | "Discontinued",
        },
      });
      // Remove the saved row + clear its extras
      const current = form.getValues("variants") ?? [];
      form.setValue("variants", current.filter((_, i) => i !== idx));
      setExtraAttrs((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: getListItemVariantsQueryKey(editItem.id) });
      queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
      toast({ title: attributeValueIds.length > 1 ? `Variant added (${attributeValueIds.length} attributes).` : "Variant added." });
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        toast({ title: "Duplicate variant — that attribute set already exists for this item.", variant: "destructive" });
      } else {
        toast({ title: "Failed to add variant.", variant: "destructive" });
      }
    }
  }

  function handleVariantDialogClose() {
    setVariantDialogOpen(false);
    setEditVariant(null);
  }

  function handleEditVariantClick(v: InventoryItemVariant) {
    setEditVariant(v);
    setVariantDialogOpen(true);
  }

  async function handleConfirmDeleteVariant() {
    if (!deleteVariantTarget || !editItem) return;
    try {
      await deleteVariantMutation.mutateAsync({
        itemId: editItem.id,
        variantId: deleteVariantTarget.id,
      });
      queryClient.invalidateQueries({
        queryKey: getListItemVariantsQueryKey(editItem.id),
      });
      queryClient.invalidateQueries({
        queryKey: getListInventoryItemsQueryKey(),
      });
      toast({ title: "Variant deleted." });
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        toast({
          title: "Cannot delete variant — has stock history. Archive instead.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Failed to delete variant.", variant: "destructive" });
      }
    } finally {
      setDeleteVariantTarget(null);
    }
  }

  const createMutation = useCreateInventoryItem();
  const updateMutation = useUpdateInventoryItem();

  const { data: categories } = useListInventoryCategories();

  // Predict the next ITM-NNNN from the items list so create-mode Item ID is auto-populated
  // (operator can still override). Server-side advisory lock is authoritative; if a race
  // produces a collision, the 409 toast handles it.
  const { data: allItems, isLoading: isLoadingAllItems } = useListInventoryItems();
  // 2026-05-19: client-side preview of the next item_code shown as the placeholder so
  // operators can see what ID they'll get. The form field stays empty in Create mode —
  // the DB trigger (migration 0026) assigns the real value based on the actual row id
  // (handles any race conditions cleanly). Preview = max(existing-id) + 1, padded to 4.
  //
  // Operator-discovered (2026-05-19) edge case: on direct page refresh, the items-list
  // query may not be resolved when the dialog renders. Gate the preview on isLoading
  // explicitly so "ITM-0001" never shows for a fraction of a second while data loads.
  const nextItemCodePreview = (() => {
    if (isLoadingAllItems || allItems === undefined) return "Loading…";
    if (allItems.length === 0) return "ITM-0001";
    const maxId = Math.max(...allItems.map((it) => it.id));
    if (!Number.isFinite(maxId)) return "ITM-0001";
    return `ITM-${String(maxId + 1).padStart(4, "0")}`;
  })();

  const form = useForm<ItemFormValues>({
    resolver: zodResolver(ItemFormSchema),
    defaultValues: {
      name: editItem?.name ?? "",
      brand: (editItem as unknown as { brand?: string | null })?.brand ?? "",
      // Edit mode: show the persisted item_code if set, else fall back to the id-derived
      // display code (itmId) so the field is never blank for legacy items. Saving will
      // persist that derived string going forward.
      itemCode: editItem ? (editItem.itemCode ?? itmId(editItem.id)) : "",
      categoryId: editItem?.categoryId ?? 0,
      parentItemId: (editItem as unknown as { parentItemId?: number | null })?.parentItemId ?? null,
      brandAgnosticSpecs: editItem?.brandAgnosticSpecs ?? "",
      hsnSacCode: editItem?.hsnSacCode ?? "",
      unitOfMeasurement: normalizeUom((editItem as unknown as { unitOfMeasurement?: string | null })?.unitOfMeasurement),
      packSize: editItem?.packSize ?? null,
      variants: [],
      // 2026-05-19: same string-from-numeric() coercion applied to price/rate fields.
      mrp: (editItem as unknown as { mrp?: number | string | null })?.mrp != null
        ? Number((editItem as unknown as { mrp?: number | string | null }).mrp)
        : null,
      photoUrls: (editItem as unknown as { photoUrls?: string[] | null })?.photoUrls ?? [],
      // 2026-05-21: Brand moved into specs. For existing items that have a brand column
      // value but no "Brand" spec row yet, inject one so it shows (and round-trips) in
      // the Specifications section.
      specs: (() => {
        const raw = (editItem as unknown as { specs?: Array<{ label: string; value: string }> | null })?.specs ?? [];
        const brand = (editItem as unknown as { brand?: string | null })?.brand ?? "";
        const hasBrandSpec = raw.some((s) => s.label.trim().toLowerCase() === "brand");
        if (brand.trim() && !hasBrandSpec) return [{ label: "Brand", value: brand }, ...raw];
        return raw;
      })(),
      chargingModel: editItem?.chargingModel as ItemFormValues["chargingModel"] ?? "Complimentary_Unlimited",
      itemKind: (editItem as unknown as { itemKind?: "Product" | "Service" })?.itemKind ?? "Product",
      // 2026-05-20: default to whole-item basis when unset (operator: "Item unit always in dropdown").
      stockOutBasis: (editItem as unknown as { stockOutBasis?: string | null })?.stockOutBasis ?? "Item",
      stockOutUnit: (editItem as unknown as { stockOutUnit?: "gram" | "kilogram" | "ml" | "litre" | null })?.stockOutUnit ?? null,
      stockOutQty: (editItem as unknown as { stockOutQty?: string | number | null })?.stockOutQty != null
        ? Number((editItem as unknown as { stockOutQty?: string | number | null }).stockOutQty)
        : null,
      // Phase 10 (Plan 10-07): freeQuantity / periodType / priceAfterLimit removed — see planQuotas[] state below.
      sellingPrice: editItem?.sellingPrice != null ? Number(editItem.sellingPrice) : null,
      memberDiscountPrice: editItem?.memberDiscountPrice != null ? Number(editItem.memberDiscountPrice) : null,
      gstRateOnSale: editItem?.gstRateOnSale != null ? Number(editItem.gstRateOnSale) : null,
      requiresPosEntry: editItem?.requiresPosEntry ?? false,
      overageRate: editItem?.overageRate != null ? Number(editItem.overageRate) : null,
      isRecipe: false,
      recipeComponents: [],
      // Phase 55 (RECIPE-SPEC-01): create-mode default. Edit mode uses
      // PreparationStepsSection (own state, own save action) below.
      preparationSteps: [],
      inventoryTracked: editItem?.inventoryTracked ?? true,
      currentStock: editItem?.currentStock != null ? Number(editItem.currentStock) : null,
      shelfLifeDays: editItem?.shelfLifeDays ?? null,
      batchTracked: editItem?.batchTracked ?? false,
      stockInFrequency: (editItem as unknown as { stockInFrequency?: string })?.stockInFrequency as ItemFormValues["stockInFrequency"] ?? "On_Demand",
      stockOutFrequency: (editItem as unknown as { stockOutFrequency?: string })?.stockOutFrequency as ItemFormValues["stockOutFrequency"] ?? "On_Demand",
      // 2026-05-19: Drizzle numeric() columns return as strings ("5.00"); Zod schema expects
      // numbers. Coerce here so existing values don't trip validation and turn labels red.
      reorderLevel: editItem?.reorderLevel != null ? Number(editItem.reorderLevel) : null,
      reorderQuantity: editItem?.reorderQuantity != null ? Number(editItem.reorderQuantity) : null,
      safetyStock: editItem?.safetyStock != null ? Number(editItem.safetyStock) : null,
      maxStockLevel: editItem?.maxStockLevel != null ? Number(editItem.maxStockLevel) : null,
      orderFrequencyDays: editItem?.orderFrequencyDays ?? null,
      standardOrderQty: editItem?.standardOrderQty != null ? Number(editItem.standardOrderQty) : null,
      dailyQuantity: editItem?.dailyQuantity != null ? Number(editItem.dailyQuantity) : null,
      status: editItem?.status as ItemFormValues["status"] ?? "Active",
      notes: editItem?.notes ?? "",
      planQuotas: [],
    },
  });

  // Watched values
  const chargingModel = useWatch({ control: form.control, name: "chargingModel" });
  const stockInFrequency = useWatch({ control: form.control, name: "stockInFrequency" });
  const watchedCategoryId = useWatch({ control: form.control, name: "categoryId" });
  const watchedVariants = useWatch({ control: form.control, name: "variants" }) ?? [];
  const watchedSpecs = useWatch({ control: form.control, name: "specs" }) ?? [];
  // 2026-05-21: variant-of-parent flow (create mode). When a parent is chosen, the new item
  // inherits the parent's spec keys (locked) + key fields are prefilled from the parent.
  const watchedParentItemId = useWatch({ control: form.control, name: "parentItemId" }) as number | null | undefined;
  const isVariantCreate = !isEditMode && watchedParentItemId != null;

  function applyParentPrefill(parentId: number) {
    const parent = (allItems ?? []).find((it) => it.id === parentId) as (InventoryItem & { specs?: Array<{ label: string; value: string }>; itemKind?: string }) | undefined;
    if (!parent) return;
    form.setValue("parentItemId", parentId);
    // Variant shares the parent's item name (locked in the form); spec values differ.
    form.setValue("name", parent.name);
    form.setValue("categoryId", parent.categoryId);
    form.setValue("hsnSacCode", parent.hsnSacCode ?? "");
    // Variant inherits the parent's GST rate (same HSN → same GST).
    if (parent.gstRateOnSale != null) form.setValue("gstRateOnSale", Number(parent.gstRateOnSale));
    form.setValue("unitOfMeasurement", parent.unitOfMeasurement as ItemFormValues["unitOfMeasurement"]);
    form.setValue("chargingModel", parent.chargingModel as ItemFormValues["chargingModel"]);
    form.setValue("itemKind", (parent.itemKind as "Product" | "Service") ?? "Product");
    // Inherit the parent's specs (keys + values) — keys are locked; operator edits values.
    // Brand now lives in specs; for a legacy parent whose Brand is only in the brand
    // column, synthesize the Brand spec row so the variant inherits it too.
    const parentSpecs = (parent.specs ?? []).map((s) => ({ label: s.label, value: s.value }));
    const parentBrand = (parent as { brand?: string | null }).brand ?? "";
    const hasBrandSpec = parentSpecs.some((s) => s.label.trim().toLowerCase() === "brand");
    form.setValue(
      "specs",
      parentBrand.trim() && !hasBrandSpec ? [{ label: "Brand", value: parentBrand }, ...parentSpecs] : parentSpecs,
    );
  }
  const isRecipeChecked = useWatch({ control: form.control, name: "isRecipe" });
  const watchedRecipeComponents = useWatch({ control: form.control, name: "recipeComponents" }) ?? [];
  // 2026-07-29: `editItem` is a snapshot of the list row held in parent state, so
  // its `hasRecipe` never flips while the dialog is open. Read the component rows
  // directly (create mode passes 0 → the generated hook's `enabled: !!itemId`
  // keeps it idle) so Margin + Preparation Steps appear the moment the first
  // component is saved, instead of only after a reopen.
  const { data: editItemRecipes = [] } = useListItemRecipes(editItem?.id ?? 0);
  const hasRecipeLive = Boolean(editItem?.hasRecipe) || editItemRecipes.length > 0;
  // Phase 55 (RECIPE-SPEC-01): recipe-card hint text on Photos + Specifications
  // shows whenever the item IS a recipe (edit mode) or is BEING MADE into a
  // recipe (create-mode toggle on). Recipe-status on existing items is carried
  // by the InventoryItem.hasRecipe correlated-subquery field (see
  // inventory-items.ts GET :id).
  const showRecipeHints = Boolean(isRecipeChecked) || hasRecipeLive;
  // Hot-fix 2026-05-14: "+ Add Variant" hides while any pending row is incomplete.
  // A row is incomplete when attributeKeyId is null OR neither attributeValueId nor
  // attributeValueText is set. Forces operator to fill the current row before adding another.
  const hasIncompleteVariantRow = watchedVariants.some((v) => {
    if (!v) return false;
    if (!v.attributeKeyId) return true;
    const hasValue = !!v.attributeValueId || (typeof v.attributeValueText === "string" && v.attributeValueText.trim().length > 0);
    return !hasValue;
  });

  // 2026-05-18: category-level "allows recipe" gate retired — any item is recipeable.
  const selectedCategory = categories?.find((c) => c.id === watchedCategoryId);
  const isRecipeAllowed = true;

  // useFieldArray for inline variant rows (create mode)
  const variantsArray = useFieldArray({ control: form.control, name: "variants" });

  // useFieldArray for inline recipe component rows (create mode)
  const recipeComponentsArray = useFieldArray({ control: form.control, name: "recipeComponents" });

  // Phase 10 — Quotas-per-Plan matrix for Complimentary_With_Limit items
  const planQuotasArray = useFieldArray({ control: form.control, name: "planQuotas" });
  // Overage Rate is the only column the operator wants a bulk-fill toggle for
  // (2026-05-18 operator revision). Free Quantity and Period vary per plan by
  // nature — Day Pass uses per_visit, Workstation uses per_month, etc. — so no
  // toggle there. Overage Rate is often the same across plans, so a single
  // toggle mirrors the top row's value to every other row.
  const [sameOverage, setSameOverage] = useState(false);
  const { data: plans } = useListInventoryPlans();
  const { data: existingPlanQuotas } = useListPlanEntitlements(
    { itemId: editItem?.id },
    {
      query: {
        queryKey: getListPlanEntitlementsQueryKey({ itemId: editItem?.id }),
        enabled: editItem != null && editItem.chargingModel === "Complimentary_With_Limit",
      },
    }
  );

  // Seed matrix on Create-mode when chargingModel becomes CWL.
  // One row per active plan with sensible defaults per cadence.
  useEffect(() => {
    if (chargingModel !== "Complimentary_With_Limit") return;
    if (editItem) return; // Create mode only
    if (!plans) return;
    if (planQuotasArray.fields.length > 0) return;
    const activePlans = plans.filter((p) => p.status === "Active");
    activePlans.forEach((p) => {
      planQuotasArray.append({
        planId: p.id,
        planName: p.planName,
        quotaQuantity: 0,
        quotaPeriod: p.cadence === "Recurring" ? "per_month" : "per_visit",
        overageRate: null,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargingModel, plans, editItem]);

  // Hydrate matrix on Edit-mode when CWL and existing entitlements arrive.
  useEffect(() => {
    if (chargingModel !== "Complimentary_With_Limit") return;
    if (!editItem) return;
    if (!plans) return;
    if (!existingPlanQuotas) return;
    const existingByPlanId = new Map(existingPlanQuotas.map((e) => [e.planId, e]));
    const rows = plans
      .filter((p) => p.status === "Active")
      .map((p) => {
        const ex = existingByPlanId.get(p.id);
        const defaultPeriod = (p.cadence === "Recurring" ? "per_month" : "per_visit") as ItemFormValues["planQuotas"][number]["quotaPeriod"];
        return {
          planId: p.id,
          planName: p.planName,
          quotaQuantity: ex ? Number(ex.quotaQuantity) : 0,
          quotaPeriod: (ex?.quotaPeriod as ItemFormValues["planQuotas"][number]["quotaPeriod"] | undefined) ?? defaultPeriod,
          overageRate: ex?.overageRate != null ? Number(ex.overageRate) : null,
        };
      });
    planQuotasArray.replace(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargingModel, editItem, existingPlanQuotas, plans]);

  // Clear matrix when chargingModel switches off CWL.
  useEffect(() => {
    if (chargingModel !== "Complimentary_With_Limit" && planQuotasArray.fields.length > 0) {
      planQuotasArray.replace([]);
      setSameOverage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargingModel]);

  // Overage Rate bulk-fill: when toggle is on, mirror the top row's overageRate
  // to every other row. Quantity + Period stay independent per row.
  const watchedTopRow = useWatch({ control: form.control, name: "planQuotas.0" });
  useEffect(() => {
    if (!sameOverage) return;
    if (planQuotasArray.fields.length <= 1) return;
    if (!watchedTopRow) return;
    planQuotasArray.fields.forEach((_, i) => {
      if (i === 0) return;
      form.setValue(`planQuotas.${i}.overageRate`, watchedTopRow.overageRate);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameOverage, watchedTopRow?.overageRate]);

  // VAL-04 (D-12): advisory toast when HSN/SAC prefix disagrees with gst_rate
  const hsnSacCode = form.watch("hsnSacCode");
  const gstRateOnSale = form.watch("gstRateOnSale");

  useEffect(() => {
    if (!hsnSacCode || gstRateOnSale == null) return;
    const ok = checkHsnGstMatch(hsnSacCode, Number(gstRateOnSale));
    if (!ok) {
      const expected = HSN_GST_ADVISORY.find((e) => hsnSacCode.startsWith(e.prefix));
      toast({
        title: "VAL-04 advisory: HSN/GST mismatch",
        description: `HSN ${hsnSacCode} typically uses ${expected?.rate}% GST, you set ${gstRateOnSale}%. Server still accepts.`,
        className: "bg-amber-100 border-amber-300",
      });
    }
  }, [hsnSacCode, gstRateOnSale]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill charging model from selected category. The model is locked at the
  // category level (no override at item level — see Section B badge); the category
  // dropdown itself shows the model after "|" so operators know what they're picking.
  useEffect(() => {
    if (!watchedCategoryId || !categories) return;
    const cat = categories.find((c) => c.id === watchedCategoryId);
    if (!cat) return;
    form.setValue("chargingModel", cat.chargingModel as ItemFormValues["chargingModel"]);
  }, [watchedCategoryId, categories, form]);

  // 2026-05-18: FORM-06 retired with the recipe gate. Recipe state is now only cleared by
  // the operator toggling Is Recipe off, never automatically by category change.

  // 2026-05-19: item_code is now derived from the row id via a DB trigger (migration 0026).
  // Operator-facing field stays blank by default in Create mode; placeholder explains what
  // the server will fill it with. Operator can still type a custom override.

  // 2026-07-24 hot-fix: without this, a zod validation failure on ANY field — including
  // ones not rendered in the dialog (e.g. a legacy unit_of_measurement) — makes Save a
  // silent no-op. Surface which field(s) blocked the save so it's never invisible again.
  function onInvalid(errors: FieldErrors<ItemFormValues>) {
    const UOM_LABELS: Record<string, string> = {
      name: "Item Name",
      categoryId: "Category",
      hsnSacCode: "HSN/SAC Code",
      mrp: "Maximum Retail Price (MRP)",
      sellingPrice: "Selling Price",
      unitOfMeasurement: "Unit of Measurement",
      stockInFrequency: "Stock-In Frequency",
      stockOutFrequency: "Stock-Out Frequency",
      planQuotas: "Plan Quotas",
      photoUrls: "Photos",
    };
    const keys = Object.keys(errors ?? {});
    const first = keys[0];
    const label = first ? (UOM_LABELS[first] ?? first) : undefined;
    toast({
      title: "Couldn't save — please fix the highlighted fields",
      description: label
        ? `Problem with: ${label}${keys.length > 1 ? ` (and ${keys.length - 1} more)` : ""}.`
        : "One or more fields are invalid.",
      variant: "destructive",
    });
  }

  async function onSubmit(values: ItemFormValues) {
    try {
      // Phase 10 — strip display-only planName before POST/PATCH
      const planQuotasPayload =
        values.chargingModel === "Complimentary_With_Limit" && values.planQuotas
          ? values.planQuotas.map(({ planName: _planName, ...rest }) => rest)
          : undefined;

      // 2026-05-21: Brand now lives as a "Brand" spec row. Derive the brand column value
      // from specs so the items-list display, variant displayName, and BrandCombobox
      // filtering keep working off the dedicated column.
      const brandFromSpecs =
        (values.specs ?? []).find((s) => s.label.trim().toLowerCase() === "brand")?.value?.trim() || null;

      if (editItem) {
        // Edit mode: standard PATCH (variants + recipe managed via edit-mode sub-sections)
        const data = {
          categoryId: values.categoryId,
          name: values.name,
          brand: brandFromSpecs,
          itemCode: values.itemCode?.trim() || null,
          brandAgnosticSpecs: values.brandAgnosticSpecs && values.brandAgnosticSpecs.length > 0 ? values.brandAgnosticSpecs : "-",
          hsnSacCode: values.hsnSacCode,
          unitOfMeasurement: values.unitOfMeasurement,
          packSize: values.packSize ?? null,
          chargingModel: values.chargingModel,
          itemKind: values.itemKind,
          stockOutBasis: values.stockOutBasis ?? null,
          stockOutUnit: values.stockOutUnit ?? null,
          stockOutQty: values.stockOutQty ?? null,
          // Phase 10 (Plan 10-07): freeQuantity / periodType / priceAfterLimit removed — see planQuotas[].
          sellingPrice: values.sellingPrice,
          memberDiscountPrice: values.memberDiscountPrice,
          gstRateOnSale: values.gstRateOnSale,
          paymentCollection: values.paymentCollection,
          requiresPosEntry: values.requiresPosEntry,
          overageRate: values.overageRate,
          inventoryTracked: true,
          currentStock: values.currentStock,
          storageLocation: values.storageLocation,
          shelfLifeDays: values.shelfLifeDays,
          batchTracked: values.batchTracked,
          stockInFrequency: values.stockInFrequency,
          stockOutFrequency: values.stockOutFrequency,
          reorderLevel: values.reorderLevel,
          reorderQuantity: values.reorderQuantity,
          safetyStock: values.safetyStock,
          maxStockLevel: values.maxStockLevel,
          orderFrequencyDays: values.orderFrequencyDays,
          standardOrderQty: values.standardOrderQty,
          dailyQuantity: values.dailyQuantity,
          status: values.status,
          notes: values.notes,
          mrp: values.mrp ?? null,
          // 2026-05-19: send photoUrls only when changed (skip key entirely to leave untouched).
          ...(values.photoUrls && values.photoUrls.length > 0 ? { photoUrls: values.photoUrls } : {}),
          // 2026-05-19: always send specs on PATCH (sentinel for "replace specs array"). [] clears.
          // Drop any half-filled spec rows so saved data is always clean.
          specs: (values.specs ?? []).filter((s) => s.label.trim() && s.value.trim()),
          planQuotas: planQuotasPayload,
        };
        await updateMutation.mutateAsync({ id: editItem.id, data });
        toast({ title: "Item saved." });
        queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
        onClose();
      } else {
        // Create mode: include variants and recipe in the atomic POST
        const body = {
          categoryId: values.categoryId,
          parentItemId: values.parentItemId ?? null,
          name: values.name,
          brand: brandFromSpecs,
          itemCode: values.itemCode?.trim() || null,
          brandAgnosticSpecs: values.brandAgnosticSpecs && values.brandAgnosticSpecs.length > 0 ? values.brandAgnosticSpecs : "-",
          hsnSacCode: values.hsnSacCode,
          unitOfMeasurement: values.unitOfMeasurement,
          packSize: values.packSize ?? null,
          chargingModel: values.chargingModel,
          itemKind: values.itemKind,
          stockOutBasis: values.stockOutBasis ?? null,
          stockOutUnit: values.stockOutUnit ?? null,
          stockOutQty: values.stockOutQty ?? null,
          // Phase 10 (Plan 10-07): freeQuantity / periodType / priceAfterLimit removed — see planQuotas[].
          sellingPrice: values.sellingPrice,
          memberDiscountPrice: values.memberDiscountPrice,
          gstRateOnSale: values.gstRateOnSale,
          paymentCollection: values.paymentCollection,
          requiresPosEntry: values.requiresPosEntry,
          overageRate: values.overageRate,
          inventoryTracked: true,
          currentStock: values.currentStock,
          storageLocation: values.storageLocation,
          shelfLifeDays: values.shelfLifeDays,
          batchTracked: values.batchTracked,
          stockInFrequency: values.stockInFrequency,
          stockOutFrequency: values.stockOutFrequency,
          reorderLevel: values.reorderLevel,
          reorderQuantity: values.reorderQuantity,
          safetyStock: values.safetyStock,
          maxStockLevel: values.maxStockLevel,
          orderFrequencyDays: values.orderFrequencyDays,
          standardOrderQty: values.standardOrderQty,
          dailyQuantity: values.dailyQuantity,
          status: values.status,
          notes: values.notes,
          mrp: values.mrp ?? null,
          // 2026-05-19 hot-fix: 1-3 photos required on Create (server enforces).
          photoUrls: values.photoUrls ?? [],
          // Phase 55 (RECIPE-SPEC-01): only send preparationSteps for a recipe
          // item — a non-recipe item has no steps by construction. Empty/
          // whitespace-only rows are dropped so saved data is always clean;
          // the server's `null-or-[]` idiom then persists [] as NULL.
          preparationSteps: values.isRecipe
            ? (values.preparationSteps ?? []).map((s) => s.trim()).filter((s) => s.length > 0)
            : [],
          // 2026-05-19: optional flat specs (Brand / Weight / Flavour / etc.) on Create.
          // Drop any half-filled spec rows so saved data is always clean.
          specs: (values.specs ?? []).filter((s) => s.label.trim() && s.value.trim()),
          // Phase 7: inline variants (only if non-empty)
          variants: values.variants && values.variants.length > 0 ? values.variants.map((v) => ({
            attributeKeyId: v.attributeKeyId ?? null,
            attributeValueId: v.attributeValueId ?? null,
            attributeValueText: v.attributeValueText || null,
            itemCode: v.itemCode || null,
            displayName: v.displayName || null,
            currentStock: v.currentStock ?? 0,
            sellingPrice: v.sellingPrice ?? null,
            status: v.status ?? "Active",
          })) : undefined,
          // Phase 7: inline recipe (only when Is Recipe is on and components are added)
          recipe: values.isRecipe && values.recipeComponents && values.recipeComponents.length > 0
            ? {
                components: values.recipeComponents.map((rc) => ({
                  componentItemId: rc.componentItemId,
                  qtyPerServe: rc.qtyPerServe,
                  consumptionMode: rc.consumptionMode ?? "tracked",
                  notes: rc.notes ?? null,
                })),
              }
            : undefined,
          // Phase 10: inline plan quotas (only for CWL items, planName stripped)
          planQuotas: planQuotasPayload,
        };
        const response = await createMutation.mutateAsync({ data: body });
        queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
        // Phase 7: MRP soft-validation — server returns 201 with warning when selling > MRP
        if (response.warning) {
          toast({
            title: "Saved with warning",
            description: response.warning,
            variant: "destructive",
          });
        } else {
          toast({ title: "Item saved." });
        }
        onClose();
      }
    } catch (err: unknown) {
      // Phase 7: 409 item code collision — keep form open, highlight field
      const errAny = err as { status?: number; response?: { status: number; data?: { error?: string } } };
      const status = errAny.status ?? errAny.response?.status;
      if (status === 409) {
        const message = (errAny.response?.data?.error) ?? "Item code already exists for another item.";
        toast({ title: "Item code conflict", description: message, variant: "destructive" });
        form.setError("itemCode", { message });
        return;
      }
      // 400 Invalid body with a photoUrls too_small detail — the server
      // requires 1-3 photos on Create. Surface a targeted toast instead of
      // the generic "Something went wrong" (2026-07-22 hot-fix).
      const details = (errAny.response?.data as { details?: Array<{ path?: (string|number)[]; code?: string; minimum?: number }> } | undefined)?.details;
      const photoUrlsError = Array.isArray(details) && details.some(
        (d) => Array.isArray(d?.path) && d.path[0] === "photoUrls" && d.code === "too_small"
      );
      if (status === 400 && photoUrlsError) {
        toast({
          title: "Add at least 1 photo",
          description: "Every new item needs 1–3 photos before it can be saved. Use the Add photo button in the Photos section.",
          variant: "destructive",
        });
        form.setError("photoUrls", { message: "At least 1 photo required" });
        return;
      }
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* 2026-05-20: flex-column layout capped at 92vh so the dialog never exceeds the
          viewport (tablet landscape has a short height). Header + footer stay fixed;
          the body flex-grows and scrolls internally so Save is always reachable. */}
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {editItem ? `Edit ${itmId(editItem.id)} — ${editItem.name}` : "Add Item"}
          </DialogTitle>
          {/* DialogDescription required by Radix for screen-reader
              support — without it Radix logs a warning every time the
              dialog mounts. Visually muted; the form body underneath
              carries the actual UI. */}
          <DialogDescription className="sr-only">
            {editItem
              ? "Edit the selected inventory item's master data, pricing, stock thresholds, plan quotas, and variants."
              : "Create a new inventory item — capture its master data, pricing, stock thresholds, and optional plan quotas or variants."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            {!bodyReady ? (
              // Skeleton placeholder shown for ~2 frames after open.
              // Same outer height as the real body so the dialog
              // doesn't resize when bodyReady flips true.
              <div className="space-y-3 p-1 animate-pulse">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-3 w-24 rounded bg-muted" />
                    <div className="h-9 w-full rounded bg-muted/70" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4 p-1">
                {/* 2026-05-21: New Item vs Variant choice (create mode only). A variant inherits
                    the parent's spec keys + key fields; you set its own values + name + price. */}
                {!isEditMode && (
                  <div className="rounded-md border bg-muted/30 p-3 space-y-3">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={watchedParentItemId == null ? "default" : "outline"}
                        onClick={() => { form.setValue("parentItemId", null); }}
                      >
                        New Item
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={watchedParentItemId != null ? "default" : "outline"}
                        onClick={() => {
                          // pick the first non-variant item as a default starting point
                          const firstParent = (allItems ?? []).find((it) => (it as { parentItemId?: number | null }).parentItemId == null);
                          if (firstParent) applyParentPrefill(firstParent.id);
                        }}
                      >
                        Variant of existing item
                      </Button>
                    </div>
                    {watchedParentItemId != null && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Parent item</p>
                        <ParentItemCombobox
                          items={(allItems ?? []).filter((it) => (it as { parentItemId?: number | null }).parentItemId == null)}
                          value={watchedParentItemId}
                          onChange={(id) => applyParentPrefill(id)}
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Specs, category, brand &amp; unit are inherited from the parent. Set this variant&rsquo;s
                          own name, price &amp; spec values below. To add a new spec, add it to the parent item first.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {/* Section A — Identification (Phase 7.1 hot-fix: Category-first ordering — Category → Name → Brand → Code) */}
                <p className="text-sm font-semibold text-foreground">Identification</p>
                <div className="space-y-3">
                  {/* Row 1: Category (gateway field) + Item ID (auto-gen) on the same row */}
                  <div className="grid grid-cols-2 gap-3">
                    <FormField name="categoryId" control={form.control} render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Category <span className="text-destructive">*</span></FormLabel>
                        <CategoryCombobox
                          value={field.value}
                          categories={categories ?? []}
                          onChange={(id) => field.onChange(id)}
                        />
                        <FormMessage />
                      </FormItem>
                    )} />

                    <FormField name="itemCode" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Item ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={editItem ? "Auto-generated" : `${nextItemCodePreview} (next available)`}
                            {...field}
                            value={field.value ?? ""}
                            disabled
                            readOnly
                            className="cursor-not-allowed bg-muted/60"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  {/* Row 2: Item Name (full-width). Brand moved into the Specifications
                      section (2026-05-21) — add a "Brand" spec row instead. */}
                  <div className="grid grid-cols-1 gap-3">
                    <FormField name="name" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Item Name <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            disabled={isVariantCreate}
                            className={isVariantCreate ? "cursor-not-allowed bg-muted/60" : undefined}
                          />
                        </FormControl>
                        {isVariantCreate && (
                          <FormDescription className="text-xs">
                            Inherited from the parent item — variants share the same name and are
                            distinguished by their spec values.
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  {/* Row 3: HSN/SAC + MRP paired (hot-fix 2026-05-14: MRP moved up from Pricing section
                      so HSN/SAC isn't full-width). Selling Price still lives in the Pricing section below. */}
                  <div className="grid grid-cols-2 gap-3">
                    <FormField name="hsnSacCode" control={form.control} render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>HSN/SAC Code <span className="text-destructive">*</span></FormLabel>
                        <HsnCodeCombobox
                          value={field.value ?? ""}
                          onChange={(code, gstRate) => {
                            field.onChange(code);
                            // Auto-fill the item's GST % from the selected HSN/SAC code.
                            if (gstRate != null) form.setValue("gstRateOnSale", gstRate, { shouldValidate: true });
                          }}
                        />
                        <FormDescription className="text-[11px]">
                          GST % auto-fills from the code. Manage codes in Inventory → HSN / GST.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField name="mrp" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Maximum Retail Price (MRP) ₹ <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Required"
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                          />
                        </FormControl>
                        {/* MRP is GST-inclusive — show the base + GST split derived from the GST %. */}
                        {(() => {
                          const mrp = Number(form.watch("mrp")) || 0;
                          const rate = Number(form.watch("gstRateOnSale")) || 0;
                          if (mrp <= 0 || rate <= 0) return null;
                          const base = mrp / (1 + rate / 100);
                          const gst = mrp - base;
                          return (
                            <p className="text-[11px] text-muted-foreground mt-1 font-mono tabular-nums">
                              Incl. {rate}% GST → base ₹{base.toFixed(2)} + GST ₹{gst.toFixed(2)}
                            </p>
                          );
                        })()}
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  {/* Row 4: Item Type (Product / Service) — operator request 2026-05-18 */}
                  <FormField name="itemKind" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>Item Type <span className="text-destructive">*</span></FormLabel>
                      <Select value={field.value ?? "Product"} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="Product">Product — physical goods (shake, biscuit, pen)</SelectItem>
                          <SelectItem value="Service">Service — intangible (print, scan, photocopy)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription className="text-[11px]">
                        Services can still draw down components via the Recipe toggle below
                        (e.g. Print A4 → paper + ink). HSN code applies to Products; SAC code to Services.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />

                  {/* Has expiry date — when ON, Stock-In prompts for an expiry date on each
                      receipt line for this item, and the receipt can't be marked Stocked
                      without one. Stored on the existing shelfLifeDays column (1 = yes, null = no). */}
                  <FormField name="shelfLifeDays" control={form.control} render={({ field }) => (
                    <FormItem className="flex items-center gap-3">
                      <FormLabel className="mt-0">Has expiry date</FormLabel>
                      <FormControl>
                        <Switch
                          checked={Number(field.value ?? 0) > 0}
                          onCheckedChange={(checked) => field.onChange(checked ? 1 : null)}
                        />
                      </FormControl>
                      <FormDescription className="text-[11px] !mt-0">
                        Turn on for food / perishables. Stock-In will require an expiry date on each receipt.
                      </FormDescription>
                    </FormItem>
                  )} />

                </div>

                <Separator className="my-4" />

                {/* 4.5 Photos (2026-05-19 hot-fix) — 1-3 per item, uploaded to Supabase Storage. */}
                <FormField
                  name="photoUrls"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Photos <span className="text-destructive">*</span></FormLabel>
                      </div>
                      <FormControl>
                        <ItemPhotosInput
                          value={field.value as string[] | null | undefined}
                          onChange={(next) => field.onChange(next)}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        At least 1 photo required, up to 3. PNG / JPEG / WebP, max 10MB each.
                      </FormDescription>
                      {/* Phase 55 (RECIPE-SPEC-01): recipe-card photo hint. */}
                      {showRecipeHints && (
                        <FormDescription className="text-xs">
                          Show the prepared dish, not the ingredients.
                        </FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator className="my-4" />

                {/* 4.7 Specifications (2026-05-19) — flat key/value attributes of THIS single SKU.
                    Not to be confused with the Variants section below, which is for items that
                    come as multiple SKUs (Pen Blue vs Pen Red — separate stock and prices). */}
                <FormField
                  name="specs"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Specifications</FormLabel>
                      <FormDescription className="text-xs">
                        Flat attributes of this single SKU: Brand, Weight, Flavour, Composition, etc.
                        Add a “Brand” row here (type “Generic” if no brand). Use Variants below only
                        when the item exists as multiple SKUs (different stock counts / prices).
                      </FormDescription>
                      {/* Phase 55 (RECIPE-SPEC-01): recipe-card specs hint. */}
                      {showRecipeHints && (
                        <FormDescription className="text-xs">
                          For prepared dishes, use recipe-card values — water, temperature, timers, seasonings, add-ons.
                        </FormDescription>
                      )}
                      <FormControl>
                        <ItemSpecsInput
                          value={field.value as SpecPair[] | null | undefined}
                          onChange={(next) => field.onChange(next)}
                          lockedKeys={isVariantCreate}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Stock-Out / Refill Basis (2026-05-20). Options derived from the
                    measure-type specs above (Weight / Volume / Piece) — Brand / Colour /
                    Flavour are excluded. For Weight/Volume a unit sub-dropdown appears. */}
                {(() => {
                  // "Item" (whole-item / pack-as-one) is always available + default. Any spec
                  // attribute can be the basis EXCEPT descriptive ones (Brand / Color / Flavour).
                  // So Pack, Weight, Volume, Piece, Size, etc. all appear. Weight/Volume also
                  // surface a unit sub-dropdown.
                  const EXCLUDE = ["brand", "color", "colour", "flavour", "flavor"];
                  const measureSpecs = watchedSpecs.filter(
                    (s) => s.label && !EXCLUDE.includes(s.label.toLowerCase()),
                  );
                  const measureOptions = Array.from(
                    new Set(
                      measureSpecs.map((s) => s.label.charAt(0).toUpperCase() + s.label.slice(1).toLowerCase()),
                    ),
                  );
                  return (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <FormField name="stockOutBasis" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Stock-Out / Refill Basis</FormLabel>
                          <Select
                            value={field.value ?? "Item"}
                            onValueChange={(v) => {
                              field.onChange(v);
                              if (v !== "Weight" && v !== "Volume") {
                                form.setValue("stockOutUnit", null);
                              } else {
                                const spec = measureSpecs.find((s) => s.label.toLowerCase() === v.toLowerCase());
                                const m = spec?.value ? /([A-Za-z]+)$/.exec(spec.value.trim()) : null;
                                const unitFromSpec = m?.[1]?.toLowerCase();
                                const allowed = v === "Weight" ? ["gram", "kilogram"] : ["ml", "litre"];
                                form.setValue(
                                  "stockOutUnit",
                                  (unitFromSpec && allowed.includes(unitFromSpec) ? unitFromSpec : allowed[0]) as "gram" | "kilogram" | "ml" | "litre",
                                );
                              }
                            }}
                          >
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="Item">Item Only</SelectItem>
                              {measureOptions.map((cap) => (
                                <SelectItem key={cap} value={cap}>{cap}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription className="text-[11px]">
                            How this item is consumed when stocked out. &ldquo;Item Only&rdquo; = the whole
                            item (e.g. a pack counted as one). Measure options come from the specs above.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )} />
                      {(form.watch("stockOutBasis") === "Weight" || form.watch("stockOutBasis") === "Volume") && (
                        <div className="space-y-2">
                          {/* Plain markup (not FormItem/FormLabel) — those require a FormField
                              context ancestor and throw when used as a bare composite wrapper. */}
                          <label className="text-sm font-medium leading-none">Value &amp; Unit</label>
                          <div className="flex gap-2">
                            <FormField name="stockOutQty" control={form.control} render={({ field }) => (
                              <FormItem className="w-28 space-y-0">
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={0}
                                    step="0.001"
                                    placeholder="e.g. 0.5"
                                    value={field.value ?? ""}
                                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                                  />
                                </FormControl>
                              </FormItem>
                            )} />
                            <FormField name="stockOutUnit" control={form.control} render={({ field }) => (
                              <FormItem className="flex-1 space-y-0">
                                <Select value={field.value ?? ""} onValueChange={field.onChange}>
                                  <FormControl><SelectTrigger><SelectValue placeholder="Unit…" /></SelectTrigger></FormControl>
                                  <SelectContent>
                                    {(form.watch("stockOutBasis") === "Weight" ? ["gram", "kilogram"] : ["ml", "litre"]).map((u) => (
                                      <SelectItem key={u} value={u}>{u}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )} />
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            Quantity consumed per stock-out (e.g. 0.5 kilogram).
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <Separator className="my-4" />

                {/* 5. Variants inline section (create mode) / edit-mode variant list.
                    2026-05-21: hidden when creating a variant item (a variant has no sub-variants). */}
                {!isVariantCreate && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">Variants <span className="font-normal text-muted-foreground">(separate SKUs only)</span></p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Use ONLY when this item exists as multiple physically separate SKUs (e.g. Pen Blue, Pen Red — different stock counts, possibly different prices). For attributes of a single SKU, use the Specifications section above instead.
                      </p>
                    </div>
                    {/* "+ Add Variant" hides while any pending row is incomplete — operator
                        must fill the current row before adding another (hot-fix 2026-05-14). */}
                    {!hasIncompleteVariantRow && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          variantsArray.append({
                            attributeKeyId: null,
                            attributeValueId: null,
                            currentStock: 0,
                            status: "Active",
                          })
                        }
                      >
                        + Add Variant
                      </Button>
                    )}
                  </div>

                  {/* Create mode: inline field-array rows */}
                  {!isEditMode && (
                    <>
                      {variantsArray.fields.length === 0 && (
                        <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
                          No variants. Leave empty to save as a single-SKU item — use Specifications above for attribute metadata.
                        </p>
                      )}
                      <div className="space-y-2">
                        {variantsArray.fields.map((field, i) => (
                          <CreateVariantRow
                            key={field.id}
                            index={i}
                            control={form.control}
                            setValue={form.setValue}
                            onRemove={() => variantsArray.remove(i)}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {/* Edit mode: existing variant list */}
                  {isEditMode && (
                    <>
                      {dialogVariants.length === 0 ? (
                        <p className="mt-2 text-sm text-muted-foreground">
                          No variants yet. Click &lsquo;+ Add Variant&rsquo; above to create one.
                        </p>
                      ) : (
                        <div className="mt-2 space-y-1">
                          {dialogVariants.map((v) => (
                            <div
                              key={v.id}
                              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                            >
                              <span className="font-medium">
                                {v.displayName ?? `Variant #${v.id}`}
                              </span>
                              <span className="font-mono text-xs text-muted-foreground mr-4">
                                Stock: {v.currentStock}
                              </span>
                              <div className="flex gap-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditVariantClick(v)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => setDeleteVariantTarget(v)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Pending inline new variants (hot-fix 2026-05-14: no modal on Add).
                          2026-05-18 composite-SKU hot-fix: each row has a "+ Add another attribute"
                          button that opens an extra (key, value) selector to define a composite
                          SKU (e.g. Pen Blue 250ml = one variant with both Blue + 250ml). */}
                      {variantsArray.fields.length > 0 && (
                        <div className="mt-3 space-y-3">
                          {variantsArray.fields.map((field, i) => {
                            const extras = extraAttrs[i] ?? [];
                            return (
                              <div key={field.id} className="rounded-md border border-dashed p-2 space-y-2">
                                <CreateVariantRow
                                  index={i}
                                  control={form.control}
                                  setValue={form.setValue}
                                  onRemove={() => { variantsArray.remove(i); removeExtraAttr(i, -1); }}
                                  trailingAction={
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={() => handleSavePendingVariant(i)}
                                      disabled={createVariantMutation.isPending}
                                    >
                                      {createVariantMutation.isPending ? "Saving…" : "Save"}
                                    </Button>
                                  }
                                />
                                {extras.map((extra, ex) => (
                                  <div key={ex} className="ml-6 grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                                    <Select
                                      value={extra.attributeKeyId?.toString() ?? ""}
                                      onValueChange={(v) => updateExtraAttr(i, ex, { attributeKeyId: Number(v), attributeValueId: null, attributeValueText: "" })}
                                    >
                                      <SelectTrigger><SelectValue placeholder="Attribute key" /></SelectTrigger>
                                      <SelectContent>
                                        {(attributeKeys ?? []).map((k) => (
                                          <SelectItem key={k.id} value={k.id.toString()}>{k.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Input
                                      type="text"
                                      placeholder="Value (e.g. 250 ml, Blue, Pack of 6)"
                                      value={extra.attributeValueText}
                                      onChange={(e) => updateExtraAttr(i, ex, { attributeValueText: e.target.value })}
                                      disabled={extra.attributeKeyId == null}
                                    />
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => removeExtraAttr(i, ex)}
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                                <div className="ml-6">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => addExtraAttr(i)}
                                    className="text-xs h-7 text-primary hover:text-primary"
                                  >
                                    <Plus className="mr-1 h-3 w-3" /> Add another attribute
                                  </Button>
                                  <span className="ml-2 text-[11px] text-muted-foreground">
                                    Composite SKU — all attributes save as one variant row.
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}

                <Separator className="my-4" />

                {/* 6. Pricing — only Pay_Per_Use needs a top-level Selling Price.
                    Complimentary_With_Limit captures per-plan Overage Rates inside the
                    Quotas per Plan matrix below (the matrix IS the source of truth;
                    no item-level overage field per operator request 2026-05-18). */}
                {chargingModel === "Pay_Per_Use" && (
                  <>
                    <p className="text-sm font-semibold text-foreground">Pricing</p>
                    <div className="space-y-3">
                      <FormField name="sellingPrice" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Selling Price ₹ <span className="text-destructive">*</span></FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="Required"
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <Separator className="my-4" />
                  </>
                )}

                {/* 7. Is Recipe toggle — CREATE MODE ONLY.
                    2026-07-29 hot-fix: this inline builder writes to the form's
                    `recipeComponents` field, which is only submitted on the
                    create POST (`body.recipe`). The edit-mode PATCH payload has
                    no recipe key, so anything typed here while editing was
                    silently dropped on Save — the operator saw "Item saved."
                    and found the ingredients gone on reopen. Edit mode now
                    renders the live RecipeBuilderSection in this same slot
                    (each component saves immediately, and existing components
                    are read back from the server). */}
                {isRecipeAllowed && !isEditMode && (
                  <div className="space-y-3">
                    <FormField name="isRecipe" control={form.control} render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Is Recipe?</FormLabel>
                          <FormDescription className="text-xs">
                            This item is prepared from underlying components on each serve.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )} />

                    {/* 8. Recipe components inline section — renders when Is Recipe is on */}
                    {isRecipeChecked && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-foreground">Recipe Components</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Add items consumed per serve. Stock_Out will decrement components, not this item.
                            </p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              recipeComponentsArray.append({
                                componentItemId: 0,
                                qtyPerServe: 0,
                                consumptionMode: "tracked",
                                notes: null,
                              })
                            }
                          >
                            + Add Component
                          </Button>
                        </div>
                        {recipeComponentsArray.fields.length === 0 && (
                          <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
                            No components yet. Click &ldquo;+ Add Component&rdquo; to define the recipe.
                          </p>
                        )}
                        <div className="space-y-2">
                          {/* create-mode only, so there is no currentItemId to self-reference */}
                          {recipeComponentsArray.fields.map((field, i) => (
                            <CreateRecipeRow
                              key={field.id}
                              index={i}
                              control={form.control}
                              setValue={form.setValue}
                              register={form.register}
                              takenItemIds={watchedRecipeComponents
                                .filter((_, j) => j !== i)
                                .map((rc) => rc?.componentItemId)
                                .filter((id): id is number => typeof id === "number" && id > 0)}
                              onRemove={() => recipeComponentsArray.remove(i)}
                            />
                          ))}
                        </div>

                        {/* Phase 55 (RECIPE-SPEC-01): Preparation Steps —
                            create-mode inline field, submitted alongside the
                            recipe on POST. Edit mode uses the separate
                            self-contained PreparationStepsSection below
                            (in Section G) that PATCHes directly. */}
                        <FormField
                          name="preparationSteps"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Preparation Steps</FormLabel>
                              <FormDescription className="text-xs">
                                Ordered recipe-card steps. Optional — you can fill these in later.
                              </FormDescription>
                              <FormControl>
                                <PreparationStepsInput
                                  value={field.value as string[] | null | undefined}
                                  onChange={(next) => field.onChange(next)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* 7-edit. Recipe — EDIT MODE. Same position in the dialog as the
                    create-mode builder above, but every action persists on the
                    spot (POST/DELETE per component) instead of travelling
                    through the outer form's Save. Margin + Preparation Steps
                    ride along because they only mean anything for a recipe. */}
                {isEditMode && editItem && (
                  <div className="space-y-3">
                    {/* Phase 55 Plan 55-04 (RECIPE-COST-02): cost-drift summary. */}
                    {hasRecipeLive && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium">Margin:</span>
                        <CostDriftPill
                          recipeCostTotal={editItem.recipeCostTotal}
                          sellingPrice={editItem.sellingPrice}
                        />
                      </div>
                    )}
                    {/* Phase 55 (RECIPE-SPEC-01): self-contained (own state, own
                        save action) so it doesn't need to travel through the
                        outer form's onSubmit. Gated on hasRecipe: not-a-recipe
                        items have no steps by construction. */}
                    {hasRecipeLive && (
                      <PreparationStepsSection
                        itemId={editItem.id}
                        itemName={editItem.name}
                        initialSteps={(editItem as unknown as { preparationSteps?: string[] | null }).preparationSteps ?? []}
                      />
                    )}
                    <RecipeBuilderSection itemId={editItem.id} itemName={editItem.name} />
                  </div>
                )}

                <Separator className="my-4" />

                {/* Section B — Usage Model (auto-derived from category) */}
                <p className="text-sm font-semibold text-foreground">
                  Usage Model
                  {chargingModel ? (
                    <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CHARGING_MODEL_COLORS[chargingModel] ?? ""}`}>
                      {chargingModel.replace(/_/g, " ")}
                    </span>
                  ) : (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">(pick a category first)</span>
                  )}
                </p>
                <div className="space-y-3">
                  {chargingModel === "Complimentary_Unlimited" && (
                    <Alert>
                      <AlertDescription>This item is free with no tracking limit.</AlertDescription>
                    </Alert>
                  )}

                  {chargingModel === "Complimentary_With_Limit" && (
                    <div className="space-y-4 rounded-md border bg-muted/30 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold">Quotas per Plan</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            One row per active plan. Each row creates a plan_entitlements row.
                          </p>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer select-none rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted shrink-0">
                          <input
                            type="checkbox"
                            checked={sameOverage}
                            onChange={(e) => setSameOverage(e.target.checked)}
                            className="h-4 w-4 accent-primary"
                          />
                          Same Overage Rate for all plans
                        </label>
                      </div>
                      {planQuotasArray.fields.length === 0 ? (
                        <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
                          Loading plans… If empty, create active plans in the Plans tab first.
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Plan</TableHead>
                              <TableHead>Free Quantity</TableHead>
                              <TableHead>Period</TableHead>
                              <TableHead>Overage Rate (₹)</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {planQuotasArray.fields.map((field, i) => {
                              const overageDisabled = sameOverage && i > 0;
                              return (
                                <TableRow key={field.id}>
                                  <TableCell className="font-medium">
                                    {(field as unknown as { planName: string }).planName.replace(/_/g, " ")}
                                  </TableCell>
                                  <TableCell>
                                    <FormField
                                      name={`planQuotas.${i}.quotaQuantity` as const}
                                      control={form.control}
                                      render={({ field: f }) => (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={f.value ?? 0}
                                          onChange={(e) =>
                                            f.onChange(e.target.value === "" ? 0 : Number(e.target.value))
                                          }
                                        />
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <FormField
                                      name={`planQuotas.${i}.quotaPeriod` as const}
                                      control={form.control}
                                      render={({ field: f }) => (
                                        <Select
                                          value={f.value}
                                          onValueChange={f.onChange}
                                        >
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {Object.values(QuotaPeriod).map((p) => (
                                              <SelectItem key={p} value={p}>
                                                {p.replace(/_/g, " ")}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <FormField
                                      name={`planQuotas.${i}.overageRate` as const}
                                      control={form.control}
                                      render={({ field: f }) => (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          placeholder="0"
                                          disabled={overageDisabled}
                                          value={f.value ?? ""}
                                          onChange={(e) =>
                                            f.onChange(e.target.value === "" ? null : Number(e.target.value))
                                          }
                                        />
                                      )}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                      {form.formState.errors.planQuotas && !Array.isArray(form.formState.errors.planQuotas) && (
                        <p className="text-xs text-destructive">
                          {String(form.formState.errors.planQuotas.message ?? "")}
                        </p>
                      )}
                    </div>
                  )}

                  {chargingModel === "Pay_Per_Use" && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <FormField name="memberDiscountPrice" control={form.control} render={({ field }) => (
                          <FormItem>
                            <FormLabel>Member Discount Price ₹</FormLabel>
                            <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField name="gstRateOnSale" control={form.control} render={({ field }) => (
                          <FormItem>
                            <FormLabel>GST Rate % <span className="text-destructive">*</span></FormLabel>
                            <Select onValueChange={(v) => field.onChange(Number(v))} value={field.value != null ? String(field.value) : ""}>
                              <FormControl>
                                <SelectTrigger><SelectValue placeholder="Select rate" /></SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="0">0%</SelectItem>
                                <SelectItem value="5">5%</SelectItem>
                                <SelectItem value="12">12%</SelectItem>
                                <SelectItem value="18">18%</SelectItem>
                                <SelectItem value="28">28%</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <FormField name="paymentCollection" control={form.control} render={({ field }) => (
                          <FormItem>
                            <FormLabel>Payment Collection</FormLabel>
                            <Select value={field.value ?? ""} onValueChange={field.onChange}>
                              <FormControl><SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger></FormControl>
                              <SelectContent>
                                <SelectItem value="immediate">Immediate</SelectItem>
                                <SelectItem value="monthly_invoice">Monthly Invoice</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )} />
                      </div>
                      <FormField name="requiresPosEntry" control={form.control} render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                          <FormLabel className="mt-0">Requires POS Entry</FormLabel>
                          <FormControl><Switch checked={field.value ?? false} onCheckedChange={field.onChange} /></FormControl>
                        </FormItem>
                      )} />
                    </div>
                  )}

                  {chargingModel === "Plan_Inclusive_Quota" && (
                    <div className="space-y-3">
                      <Alert>
                        <AlertDescription>Quotas are defined per plan in the Entitlements tab — no quota fields here.</AlertDescription>
                      </Alert>
                      <FormField name="overageRate" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Overage Rate ₹</FormLabel>
                          <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>
                  )}

                  {chargingModel === "AddOn_Optional" && (
                    <div className="space-y-3">
                      <FormField name="gstRateOnSale" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>GST Rate %</FormLabel>
                          <Select onValueChange={(v) => field.onChange(Number(v))} value={field.value != null ? String(field.value) : ""}>
                            <FormControl>
                              <SelectTrigger><SelectValue placeholder="Select rate" /></SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="0">0%</SelectItem>
                              <SelectItem value="5">5%</SelectItem>
                              <SelectItem value="12">12%</SelectItem>
                              <SelectItem value="18">18%</SelectItem>
                              <SelectItem value="28">28%</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>
                  )}
                </div>

                <Separator className="my-4" />

                {/* Section C (Storage & Handling) removed from the form per operator request 2026-05-14.
                    DB columns storage_location / shelf_life_days / batch_tracked stay; legacy values
                    are loaded into form state via defaultValues and preserved on submit, just not
                    surfaced here. To re-introduce, restore the FormField block from git history. */}

                {/* Section D — Stock Frequency (Phase 2.1 D-17) */}
                <p className="text-sm font-semibold text-foreground">Stock Frequency</p>
                <div className="space-y-3">
                  {/* Opening Stock — editable on Create; read-only display on Edit with
                      last-purchase context. operator request 2026-05-18 (both halves).
                      2026-10-16: the API no longer writes this straight onto the item row —
                      it books an Opening_Stock receipt (routes/inventory-items.ts Step 4.6).
                      The old copy here said "no Stock_In transaction needed", which read as
                      permission to ALSO record the delivery's Stock-In; four items were
                      double-counted on 2026-10-14. The warning below exists to stop that. */}
                  {!isEditMode ? (
                    <FormField name="currentStock" control={form.control} render={({ field }) => (
                      <FormItem className="rounded-md border bg-muted/30 p-3">
                        <FormLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                          Opening Stock
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0 (leave empty if no stock on hand)"
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                          />
                        </FormControl>
                        <FormDescription className="text-[11px]">
                          Quantity physically on hand right now. Recorded for you as an Opening_Stock
                          receipt, so it shows up in the Stock-In register.
                          <strong className="block mt-1 text-amber-700 dark:text-amber-500">
                            Do not also record a Stock-In for these same goods — they would be counted twice.
                          </strong>
                          Leave blank if starting from zero.
                        </FormDescription>
                      </FormItem>
                    )} />
                  ) : editItem ? (
                    <div className="rounded-md border bg-muted/30 p-3">
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Existing Quantity</p>
                          <p className="text-2xl font-semibold tabular-nums">
                            {editItem.currentStock} <span className="text-base font-normal text-muted-foreground">{editItem.unitOfMeasurement}</span>
                          </p>
                        </div>
                        {editItem.lastPurchasedDate && (
                          <div className="text-right">
                            <p className="text-[11px] text-muted-foreground">Last Stock-In</p>
                            <p className="text-xs font-medium">
                              {new Date(editItem.lastPurchasedDate).toLocaleDateString()}
                              {editItem.lastPurchasedPrice ? ` · ₹${editItem.lastPurchasedPrice}/unit` : ""}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField name="stockInFrequency" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Stock-In Frequency <span className="text-destructive">*</span></FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger></FormControl>
                          <SelectContent>
                            {Object.values(StockFrequency).map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField name="stockOutFrequency" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Stock-Out Frequency <span className="text-destructive">*</span></FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger></FormControl>
                          <SelectContent>
                            {Object.values(StockFrequency).map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  {stockInFrequency === "Daily" && (
                    <div className="grid grid-cols-2 gap-3">
                      <FormField name="dailyQuantity" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Daily Quantity</FormLabel>
                          <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                        </FormItem>
                      )} />
                      <FormField name="standardOrderQty" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Standard Order Qty</FormLabel>
                          <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                        </FormItem>
                      )} />
                    </div>
                  )}

                  {stockInFrequency === "Weekly" && (
                    <div className="grid grid-cols-2 gap-3">
                      <FormField name="orderFrequencyDays" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Order Frequency (days)</FormLabel>
                          <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                        </FormItem>
                      )} />
                      <FormField name="standardOrderQty" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Standard Order Qty</FormLabel>
                          <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                        </FormItem>
                      )} />
                    </div>
                  )}

                  {stockInFrequency === "On_Demand" && (
                    <div className="grid grid-cols-2 gap-3">
                      <FormField name="reorderLevel" control={form.control} render={({ field }) => {
                        const lastMonthUse = editItem ? Number((editItem as { lastMonthUsage?: string | null }).lastMonthUsage ?? 0) : 0;
                        // Half a month's usage is a defensible threshold: 80% alert at 40% used,
                        // 90% at 45%, 100% at 50% — gives time to reorder before stock-out.
                        const suggestedLevel = lastMonthUse > 0 ? Math.ceil(lastMonthUse / 2) : 0;
                        const unit = editItem?.unitOfMeasurement ?? "";
                        return (
                          <FormItem>
                            <FormLabel>Reorder Level</FormLabel>
                            <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                            <FormDescription className="text-[11px]">
                              Triggers alert ladder: Green @ 80% · Red @ 90% · Black @ 100% of this level.
                            </FormDescription>
                            {suggestedLevel > 0 && (
                              <button
                                type="button"
                                onClick={() => field.onChange(suggestedLevel)}
                                className="mt-1 text-[11px] rounded-md border border-dashed px-2 py-0.5 hover:bg-muted hover:border-solid transition"
                              >
                                Suggested: <span className="font-medium">{suggestedLevel} {unit}</span> <span className="text-muted-foreground">(½ of last month&rsquo;s use)</span>
                              </button>
                            )}
                          </FormItem>
                        );
                      }} />
                      <FormField name="reorderQuantity" control={form.control} render={({ field }) => {
                        const lastStockIn = editItem ? Number((editItem as { lastStockInQty?: string | null }).lastStockInQty ?? 0) : 0;
                        const lastMonthUse = editItem ? Number((editItem as { lastMonthUsage?: string | null }).lastMonthUsage ?? 0) : 0;
                        const unit = editItem?.unitOfMeasurement ?? "";
                        return (
                          <FormItem>
                            <FormLabel>Reorder Quantity</FormLabel>
                            <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                            {/* 2026-05-19: clickable suggestions sourced from stock-in history + last 30 days' usage. */}
                            {(lastStockIn > 0 || lastMonthUse > 0) && (
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                {lastStockIn > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => field.onChange(lastStockIn)}
                                    className="text-[11px] rounded-md border border-dashed px-2 py-0.5 hover:bg-muted hover:border-solid transition"
                                  >
                                    Last stock-in: <span className="font-medium">{lastStockIn} {unit}</span>
                                  </button>
                                )}
                                {lastMonthUse > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => field.onChange(lastMonthUse)}
                                    className="text-[11px] rounded-md border border-dashed px-2 py-0.5 hover:bg-muted hover:border-solid transition"
                                  >
                                    Last 30 days&rsquo; usage: <span className="font-medium">{lastMonthUse} {unit}</span>
                                  </button>
                                )}
                              </div>
                            )}
                            {editItem?.lastPurchasedDate && (
                              <FormDescription className="text-[11px]">
                                Last purchased {new Date(editItem.lastPurchasedDate).toLocaleDateString()}
                                {editItem.lastPurchasedPrice ? ` at ₹${editItem.lastPurchasedPrice}/unit` : ""}.
                              </FormDescription>
                            )}
                          </FormItem>
                        );
                      }} />
                      <FormField name="safetyStock" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Safety Stock</FormLabel>
                          <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                        </FormItem>
                      )} />
                      <FormField name="maxStockLevel" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Max Stock Level</FormLabel>
                          <FormControl><Input type="number" {...field} value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} /></FormControl>
                        </FormItem>
                      )} />
                    </div>
                  )}
                  {/* Monthly: graceful degrade — no extra sub-fields rendered. (Phase 2.1 D-17, N5) */}
                </div>

                <Separator className="my-4" />

                {/* Notes (Status moved out of the form per operator request 2026-05-18 —
                    now a quick-toggle CTA in the modal footer + row action). */}
                <p className="text-sm font-semibold text-foreground">Notes</p>
                <div className="space-y-3">
                  <FormField name="notes" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormControl><Textarea placeholder="Optional notes" {...field} value={field.value ?? ""} /></FormControl>
                    </FormItem>
                  )} />
                </div>

                {/* Phase 2.1 D-23: read-only price history (live data) — edit mode only */}
                {editItem ? (
                  <>
                    <Separator className="my-4" />
                    <ItemsPriceHistoryRow itemId={editItem.id} itemName={editItem.name} />
                  </>
                ) : null}

                {/* Section G — Modifiers (edit mode only). 2026-05-18: category
                    allowsRecipe gate retired — the Modifier builder shows for any item.
                    2026-07-29: Margin / Preparation Steps / RecipeBuilderSection moved
                    up to the Is-Recipe slot so edit mode shows the recipe exactly where
                    create mode does. */}
                {isEditMode && editItem && (
                  <ModifierListSection itemId={editItem.id} itemName={editItem.name} />
                )}
              </div>
            )}
            </div>
            <DialogFooter className="mt-4 gap-2 sm:gap-2 shrink-0 border-t pt-3">
              {isEditMode && editItem && (
                <Button
                  type="button"
                  variant={editItem.status === "Active" ? "outline" : "secondary"}
                  className={editItem.status === "Active" ? "text-destructive hover:text-destructive hover:bg-destructive/10 mr-auto" : "mr-auto"}
                  disabled={updateMutation.isPending}
                  onClick={async () => {
                    try {
                      const next = editItem.status === "Active" ? "Discontinued" : "Active";
                      await updateMutation.mutateAsync({ id: editItem.id, data: { status: next } });
                      toast({ title: next === "Active" ? `${editItem.name} activated.` : `${editItem.name} deactivated.` });
                      queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
                      onClose();
                    } catch {
                      toast({ title: "Status change failed.", variant: "destructive" });
                    }
                  }}
                >
                  {editItem.status === "Active" ? "Deactivate" : "Activate"}
                </Button>
              )}
              <Button type="button" variant="outline" onClick={onClose}>Discard changes</Button>
              {/* 2026-07-22 — pre-flight gate the Save button when creating
                  a new item with 0 photos (server requires 1-3). In edit
                  mode, photoUrls is optional (send only when changed), so
                  the gate is create-only. */}
              {(() => {
                const isCreate = editItem === null;
                const photos = (form.watch("photoUrls") as string[] | undefined) ?? [];
                const noPhotos = isCreate && photos.length === 0;
                const isBusy = createMutation.isPending || updateMutation.isPending;
                return (
                  <Button
                    type="submit"
                    disabled={isBusy || noPhotos}
                    title={noPhotos ? "Upload at least 1 photo before saving" : undefined}
                  >
                    {isBusy ? "Saving…" : "Save Item"}
                  </Button>
                );
              })()}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {/* Variant create/edit dialog — sits outside Form to avoid nesting issues */}
      {editItem && (
        <VariantDialog
          key={editVariant ? `edit-${editVariant.id}` : "create-dialog"}
          open={variantDialogOpen}
          onOpenChange={(o) => { if (!o) handleVariantDialogClose(); else setVariantDialogOpen(true); }}
          mode={editVariant ? "edit" : "create"}
          itemId={editItem.id}
          parentItemName={editItem.name}
          variant={editVariant ?? undefined}
        />
      )}

      {/* Variant delete confirmation */}
      <AlertDialog
        open={deleteVariantTarget != null}
        onOpenChange={(o) => { if (!o) setDeleteVariantTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete variant?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <strong>
                {deleteVariantTarget?.displayName ?? `Variant #${deleteVariantTarget?.id}`}
              </strong>
              . If this variant has stock history, deletion will be blocked — archive it instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDeleteVariant}
              disabled={deleteVariantMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

export function ItemsTab({ openCreate, setOpenCreate }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editItem, setEditItem] = useState<InventoryItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryItem | null>(null);
  const [historyTarget, setHistoryTarget] = useState<InventoryItem | null>(null);
  const [filterCategoryId, setFilterCategoryId] = useState<number | undefined>(undefined);
  const [filterChargingModel, setFilterChargingModel] = useState<string | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Track which items have their variant sub-table expanded (keyed by item.id)
  const [openStates, setOpenStates] = useState<Record<number, boolean>>({});

  const deleteItemMutation = useDeleteInventoryItem();

  async function handleDeleteItem() {
    if (!deleteTarget) return;
    try {
      await deleteItemMutation.mutateAsync({ id: deleteTarget.id });
      toast({ title: `Deleted ${deleteTarget.name}.` });
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
    } catch (err: unknown) {
      const status = (err as { status?: number; response?: { status?: number } })?.status
        ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        const apiMessage = (err as { response?: { data?: { error?: string } }; data?: { error?: string }; body?: { error?: string } })?.response?.data?.error
          ?? (err as { data?: { error?: string } })?.data?.error
          ?? (err as { body?: { error?: string } })?.body?.error;
        toast({
          title: `Item ${deleteTarget.name} is in use.`,
          description: apiMessage ?? "Remove blocking references (vendor pricing, plan entitlements, recipes using this as a component) first, then retry.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Delete failed. See console for details.", variant: "destructive" });
      }
    }
  }

  const { data: items, isLoading } = useListInventoryItems({
    categoryId: filterCategoryId,
    chargingModel: filterChargingModel as typeof ChargingModel[keyof typeof ChargingModel] | undefined,
    status: filterStatus as "Active" | "Discontinued" | "Seasonal" | undefined,
  });
  // 2026-05-21: parent-child variants. Items created via "Variant of …" carry a
  // parentItemId; they're hidden from the top-level list and shown nested (expandable)
  // under their parent. Build parentId → children[] once per render.
  const childrenByParent = (() => {
    const m = new Map<number, NonNullable<typeof items>>();
    for (const it of items ?? []) {
      const pid = it.parentItemId;
      if (pid != null) {
        const arr = m.get(pid) ?? [];
        arr.push(it);
        m.set(pid, arr);
      }
    }
    return m;
  })();
  const matchesSearch = (it: NonNullable<typeof items>[number]) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      it.name.toLowerCase().includes(q) ||
      (it.itemCode ?? "").toLowerCase().includes(q) ||
      (it.brand ?? "").toLowerCase().includes(q) ||
      String(it.id).includes(q)
    );
  };
  const filteredItems = items?.filter((it) => {
    // Children never appear at the top level — they nest under their parent.
    if (it.parentItemId != null) return false;
    // A parent/standalone shows if it matches, OR any of its children match
    // (so searching a variant surfaces the parent to expand).
    if (matchesSearch(it)) return true;
    return (childrenByParent.get(it.id) ?? []).some(matchesSearch);
  });
  // Client-side pagination over top-level rows. Endpoint stays a bare array so the
  // many item comboboxes/forms keep receiving the full list. Filters (category/
  // status) are already server-side; search + paging are client-side here.
  const totalItems = filteredItems?.length ?? 0;
  // Items table rendering reverted to plain `pagedItems.map(...)`. The
  // @tanstack/react-table wrapper was an internal refactor (commit
  // 6e1e550) that added no user-facing value here — no sort, no
  // filter, no virtualization, no sub-rows — but its unstable args
  // (`data` slice + `getCoreRowModel()` call on every render)
  // triggered the Add Item freeze. Going back to vanilla iteration
  // removes the entire failure mode. The other Inventory Op tables
  // (Stock-In Receipts / Daily Pickups / Orders) still use
  // useReactTable with the memoised-args fix; they don't crash
  // because their host components re-render far less often.
  const pagedItems = useMemo(
    () => (filteredItems ?? []).slice((page - 1) * pageSize, page * pageSize),
    [filteredItems, page, pageSize],
  );

  // ResizeObserver on the sticky filter strip — writes the exact
  // pixel where the strip's bottom edge sits when stuck onto
  // documentElement as --items-thead-top. The table's <thead>
  // consumes it via style={{ top: 'var(--items-thead-top, 260px)' }}
  // so the pin offset stays correct regardless of how the filter
  // bar wraps on narrow viewports.
  const itemsStripRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = itemsStripRef.current;
    if (!el) return;
    const measure = () => {
      const stickyTop = parseFloat(getComputedStyle(el).top) || 0;
      const total = stickyTop + el.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--items-thead-top",
        `${Math.max(0, total - 24)}px`,
      );
    };
    requestAnimationFrame(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, filterCategoryId, filterChargingModel, filterStatus, pageSize]);
  const { data: categories } = useListInventoryCategories();

  // ---- Multi-select bulk actions ----
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<null | "delete" | "discontinue">(null);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [bulkMoveCategoryId, setBulkMoveCategoryId] = useState<number | null>(null);
  const [bulkUsageModelOpen, setBulkUsageModelOpen] = useState(false);
  const [bulkUsageModel, setBulkUsageModel] = useState<keyof typeof ChargingModel | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const updateItemMutation = useUpdateInventoryItem();

  function toggleSelectMode() {
    setSelectMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  }
  function toggleRow(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAllVisible(checked: boolean) {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set((filteredItems ?? []).map((it) => it.id)));
  }
  const visibleItemIds = (filteredItems ?? []).map((it) => it.id);
  const allVisibleItemsSelected = visibleItemIds.length > 0 && visibleItemIds.every((id) => selectedIds.has(id));

  async function runBulkItemMutation(
    op: (id: number) => Promise<void>,
    successMessage: (n: number) => string,
    fkLabel: string,
  ) {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    let ok = 0;
    let fkBlocked = 0;
    let networkErr = 0;
    let otherErr = 0;
    for (const id of ids) {
      try {
        await op(id);
        ok++;
      } catch (err: unknown) {
        const status = (err as { status?: number; response?: { status?: number } })?.status
          ?? (err as { response?: { status?: number } })?.response?.status
          ?? 0;
        const message = String((err as { message?: string })?.message ?? "");
        if (status === 409) fkBlocked++;
        else if (status === 0 || message.includes("fetch") || message.includes("Network")) networkErr++;
        else otherErr++;
      }
    }
    queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
    const totalFailed = fkBlocked + networkErr + otherErr;
    if (totalFailed === 0) {
      toast({ title: successMessage(ok) });
    } else if (networkErr === ids.length) {
      toast({ title: "API unreachable — is the server running?", variant: "destructive" });
    } else if (fkBlocked > 0 && networkErr === 0 && otherErr === 0) {
      toast({
        title: `${ok} succeeded · ${fkBlocked} ${fkLabel}`,
        description: "Items with vendor pricing, plan entitlements, or other dependencies cannot be deleted. The API returned the specific blocker for each row — check the network tab for details, or delete one at a time to see the exact reason.",
        variant: "destructive",
      });
    } else {
      const reasons: string[] = [];
      if (fkBlocked > 0) reasons.push(`${fkBlocked} ${fkLabel}`);
      if (networkErr > 0) reasons.push(`${networkErr} network error`);
      if (otherErr > 0) reasons.push(`${otherErr} other`);
      toast({
        title: `${ok} succeeded · ${reasons.join(" · ")}`,
        variant: "destructive",
      });
    }
    setSelectedIds(new Set());
    setBulkBusy(false);
    setBulkConfirm(null);
    setBulkMoveOpen(false);
    setBulkUsageModelOpen(false);
  }

  async function handleBulkDeleteItems() {
    await runBulkItemMutation(
      (id) => deleteItemMutation.mutateAsync({ id }).then(() => undefined),
      (n) => `Deleted ${n} items.`,
      "in use",
    );
  }
  async function handleBulkDiscontinue() {
    await runBulkItemMutation(
      (id) => updateItemMutation.mutateAsync({ id, data: { status: "Discontinued" } }).then(() => undefined),
      (n) => `Marked ${n} items as Discontinued.`,
      "blocked",
    );
  }
  async function handleBulkMoveCategory() {
    if (bulkMoveCategoryId == null) return;
    await runBulkItemMutation(
      (id) => updateItemMutation.mutateAsync({ id, data: { categoryId: bulkMoveCategoryId } }).then(() => undefined),
      (n) => `Moved ${n} items to a new category.`,
      "blocked",
    );
  }
  async function handleBulkChangeUsageModel() {
    if (!bulkUsageModel) return;
    await runBulkItemMutation(
      (id) => updateItemMutation.mutateAsync({ id, data: { chargingModel: bulkUsageModel as typeof ChargingModel[keyof typeof ChargingModel] } }).then(() => undefined),
      (n) => `Changed usage model on ${n} items.`,
      "blocked",
    );
  }

  const isDialogOpen = openCreate || editItem !== null;

  function handleClose() {
    setOpenCreate(false);
    setEditItem(null);
  }

  function toggleItemExpand(itemId: number) {
    setOpenStates((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  }

  // Export items to a CSV the browser downloads client-side. The Export button is a
  // dropdown: "All usage models" exports everything currently in view, or pick one
  // usage model (Complimentary, Pay Per Use, …) to export only those items. Either
  // way it honours the active search/category/status filters and includes variant
  // children (in table order) with a Variant Of back-reference. No server round-trip.
  function handleExportCsv(model?: string) {
    const tops = filteredItems ?? [];
    // Flatten to display order: each parent, then its variant children.
    const flat: Array<{ it: NonNullable<typeof tops>[number]; parentCode: string }> = [];
    for (const parent of tops) {
      flat.push({ it: parent, parentCode: "" });
      for (const child of childrenByParent.get(parent.id) ?? []) {
        flat.push({ it: child, parentCode: itmId(parent.id) });
      }
    }
    const selected = model ? flat.filter(({ it }) => it.chargingModel === model) : flat;
    if (selected.length === 0) {
      toast({ title: "Nothing to export", description: "No items match this usage model." });
      return;
    }
    const catName = (id: number) => categories?.find((c) => c.id === id)?.name ?? "";
    const headers = [
      "Item Code", "Name", "Category", "Brand", "Usage Model", "Unit",
      "Current Stock", "Status", "Kind", "Specs", "Variant Of",
    ];
    // RFC-4180 escaping: wrap in quotes and double any embedded quotes.
    const esc = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const line = ({ it, parentCode }: (typeof selected)[number]) => [
      itmId(it.id),
      it.name,
      catName(it.categoryId),
      it.brand ?? "",
      it.chargingModel.replace(/_/g, " "),
      it.unitOfMeasurement,
      formatStock(it.currentStock, it.unitOfMeasurement),
      it.status,
      (it as { itemKind?: string }).itemKind ?? "Product",
      specsToLine(it.specs),
      parentCode,
    ].map(esc).join(",");

    const csv = [headers.join(","), ...selected.map(line)].join("\r\n");
    // Prepend a BOM so Excel opens UTF-8 (₹, accented names) correctly.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const slug = model ? "-" + model.toLowerCase() : "";
    a.download = `items${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    // Natural page-scroll layout + sticky filter strip. The strip
    // overlaps the parent sticky block (page header + tabs + CTA)
    // by ~10-16px so its z-30 bg covers any height drift between
    // the two regions — no gap can open.
    <>
      {/* pt-2 + pb-8 — extra bg-background safety zone below filter
          row to cover any sub-pixel seam vs the items <thead> pin. */}
      <div
        ref={itemsStripRef}
        className="sticky top-[150px] md:top-[180px] z-20 -mx-2 px-2 pt-2 pb-8 mb-4 bg-background md:-mx-6 md:px-6 flex gap-2 flex-wrap items-center"
      >
        <Input
          type="search"
          placeholder="Search by name or item code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-[260px]"
        />
        <CategoryFilterCombobox
          value={filterCategoryId}
          categories={categories ?? []}
          onChange={setFilterCategoryId}
        />
        <Select value={filterChargingModel ?? "all"} onValueChange={(v) => setFilterChargingModel(v === "all" ? undefined : v)}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="All usage models" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All usage models</SelectItem>
            {Object.values(ChargingModel).map((m) => (
              <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus ?? "all"} onValueChange={(v) => setFilterStatus(v === "all" ? undefined : v)}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Discontinued">Discontinued</SelectItem>
            <SelectItem value="Seasonal">Seasonal</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={selectMode ? "secondary" : "outline"}
          size="sm"
          onClick={toggleSelectMode}
        >
          {selectMode ? <X className="mr-2 h-4 w-4" /> : <CheckSquare className="mr-2 h-4 w-4" />}
          {selectMode ? "Cancel" : "Select"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setImportOpen(true)}
          data-testid="button-open-import-items"
        >
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Import CSV
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={!filteredItems || filteredItems.length === 0}
              data-testid="button-export-items"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Download as CSV</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {Object.values(ChargingModel).map((m) => (
              <DropdownMenuItem key={m} onClick={() => handleExportCsv(m)}>
                {m.replace(/_/g, " ")}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {selectMode && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 rounded-full border bg-background/95 pl-4 pr-2 py-1.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 max-w-[calc(100vw-2rem)]">
          <span className="text-sm font-medium pr-2 whitespace-nowrap">{selectedIds.size} selected</span>
          <div className="h-5 w-px bg-border" />
          <Button
            size="sm"
            variant="ghost"
            disabled={bulkBusy}
            onClick={() => setBulkConfirm("delete")}
            className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="mr-2 h-4 w-4" />Delete
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={bulkBusy}
            onClick={() => setBulkConfirm("discontinue")}
            className="rounded-full"
          >
            <EyeOff className="mr-2 h-4 w-4" />Mark Discontinued
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={bulkBusy}
            onClick={() => { setBulkMoveCategoryId(null); setBulkMoveOpen(true); }}
            className="rounded-full"
          >
            <FolderInput className="mr-2 h-4 w-4" />Move to Category
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={bulkBusy}
            onClick={() => { setBulkUsageModel(null); setBulkUsageModelOpen(true); }}
            className="rounded-full"
          >
            <Tag className="mr-2 h-4 w-4" />Change Usage Model
          </Button>
          <div className="h-5 w-px bg-border ml-1" />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
            disabled={bulkBusy}
            className="rounded-full"
          >
            Clear
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Items{" "}
            <span className="text-muted-foreground font-normal text-sm">
              {filteredItems ? `(${filteredItems.length}${search && items && filteredItems.length !== items.length ? ` of ${items.length}` : ""})` : ""}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* thead pins below the upper sticky stack (parent block +
              filter strip). Sticky resolves against main (page
              scroll container). */}
          <table className="w-full caption-bottom text-sm">
            <TableHeader
              className="sticky z-10 bg-background [&_th]:bg-background"
              style={{ top: "var(--items-thead-top, 260px)" }}
            >
              <TableRow>
                {selectMode && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allVisibleItemsSelected}
                      onCheckedChange={(c) => selectAllVisible(Boolean(c))}
                      aria-label="Select all visible items"
                    />
                  </TableHead>
                )}
                <TableHead className="w-6" />
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Usage Model</TableHead>
                {/* Phase 55 Plan 55-04 (RECIPE-COST-02): cost-drift indicator
                    for recipe items. Non-recipe rows render an em-dash. */}
                <TableHead>Margin</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right w-[220px] pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {selectMode && <TableCell><Skeleton className="h-4 w-4" /></TableCell>}
                    {/* Phase 55 Plan 55-04: +1 skeleton cell for the new Margin column. */}
                    {[...Array(7)].map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredItems?.length === 0 ? (
                <TableRow>
                  {/* Phase 55 Plan 55-04: +1 to colSpan for the new Margin column. */}
                  <TableCell colSpan={selectMode ? 8 : 7} className="h-24 text-center text-muted-foreground">
                    No items yet. Add a category first, then add items under it.
                  </TableCell>
                </TableRow>
              ) : (
                pagedItems.map((item) => {
                  const childItems = childrenByParent.get(item.id) ?? [];
                  const hasVariants = childItems.length > 0;
                  const specsLine = specsToLine(item.specs);
                  const isExpanded = openStates[item.id] === true;

                  return (
                    <Collapsible
                      key={item.id}
                      open={isExpanded}
                      onOpenChange={() => hasVariants && toggleItemExpand(item.id)}
                      asChild
                    >
                      <>
                        <TableRow data-state={selectedIds.has(item.id) ? "selected" : undefined}>
                          {selectMode && (
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.has(item.id)}
                                onCheckedChange={() => toggleRow(item.id)}
                                aria-label={`Select ${item.name}`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="w-6 pr-0">
                            {hasVariants ? (
                              <CollapsibleTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  aria-label={isExpanded ? "Collapse variants" : "Expand variants"}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            ) : (
                              <span className="inline-block w-6" />
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{itmId(item.id)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{item.name}</span>
                              {(item as unknown as { itemKind?: string }).itemKind === "Service" && (
                                <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                                  Service
                                </Badge>
                              )}
                              {hasVariants && (
                                <Badge variant="secondary" className="text-xs">
                                  {childItems.length} variant{childItems.length === 1 ? "" : "s"}
                                </Badge>
                              )}
                              {/* Phase 55 Plan 55-04 (RECIPE-DISPLAY-01):
                                  swap the plain outline badge for the shared
                                  brand-accent Recipe pill used everywhere. */}
                              {item.hasRecipe && <RecipePill />}
                              {/* Expiry-tracked items (shelfLifeDays > 0) get
                                  an amber badge so the operator can spot
                                  perishables at a glance. Stock-In requires
                                  an expiry date per receipt for these. */}
                              {Number(item.shelfLifeDays ?? 0) > 0 && (
                                <Badge variant="outline" className="text-xs border-amber-300 bg-amber-50 text-amber-800">
                                  Expires
                                </Badge>
                              )}
                            </div>
                            {specsLine && (
                              <div className="text-xs text-muted-foreground">{specsLine}</div>
                            )}
                            {/* Phase 55 Plan 55-04 (RECIPE-DISPLAY-01): recipe
                                items have no meaningful currentStock — hide
                                the stock text entirely (the Recipe pill in
                                the badge row already communicates status). */}
                            {!item.hasRecipe && (
                              <div className="text-xs text-muted-foreground font-mono">
                                {formatStock(item.currentStock, item.unitOfMeasurement)}
                                {hasVariants && (
                                  <span className="ml-1 not-italic">· {childItems.length} variant{childItems.length === 1 ? "" : "s"} below</span>
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CHARGING_MODEL_COLORS[item.chargingModel] ?? ""}`}>
                              {item.chargingModel.replace(/_/g, " ")}
                            </span>
                          </TableCell>
                          {/* Phase 55 Plan 55-04 (RECIPE-COST-02): margin
                              cost-drift indicator. Non-recipe items have no
                              cost-drift concept — render an em-dash. */}
                          <TableCell>
                            {item.hasRecipe ? (
                              <CostDriftPill
                                recipeCostTotal={item.recipeCostTotal}
                                sellingPrice={item.sellingPrice}
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusWithAlertDot
                              status={item.status}
                              currentStock={item.currentStock}
                              reorderLevel={item.reorderLevel}
                              safetyStock={item.safetyStock}
                              maxStockLevel={item.maxStockLevel}
                              inventoryTracked={item.inventoryTracked}
                            />
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap pr-4">
                            <div className="flex justify-end items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={updateItemMutation.isPending}
                                onClick={async () => {
                                  const next = item.status === "Active" ? "Discontinued" : "Active";
                                  try {
                                    await updateItemMutation.mutateAsync({ id: item.id, data: { status: next } });
                                    toast({ title: next === "Active" ? `${item.name} activated.` : `${item.name} deactivated.` });
                                    queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
                                  } catch {
                                    toast({ title: "Status change failed.", variant: "destructive" });
                                  }
                                }}
                                title={item.status === "Active" ? "Deactivate item" : "Activate item"}
                                className={item.status === "Active" ? "" : "text-muted-foreground"}
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setHistoryTarget(item)}
                                title="View history"
                              >
                                <HistoryIcon className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditItem(item)}>Edit</Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => setDeleteTarget(item)}
                              >
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {hasVariants && (
                          <CollapsibleContent asChild>
                            <TableRow className="bg-muted/30 hover:bg-muted/30">
                              {/* Phase 55 Plan 55-04: +1 to colSpan for the new Margin column. */}
                              <TableCell colSpan={selectMode ? 8 : 7} className="p-0">
                                <div className="px-10 py-2 space-y-1">
                                  {childItems.map((child) => {
                                    const childSpecs = specsToLine(child.specs);
                                    return (
                                      <div
                                        key={child.id}
                                        className="flex items-center gap-3 rounded border bg-background px-3 py-1.5"
                                      >
                                        <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{itmId(child.id)}</span>
                                        <div className="min-w-0 flex-1">
                                          <span className="font-medium text-sm">{child.name}</span>
                                          {childSpecs && (
                                            <span className="ml-2 text-xs text-muted-foreground">{childSpecs}</span>
                                          )}
                                        </div>
                                        <span className="font-mono text-xs whitespace-nowrap">
                                          {formatStock(child.currentStock, child.unitOfMeasurement)}
                                        </span>
                                        <span className={`text-xs whitespace-nowrap ${child.status === "Active" ? "text-green-700" : "text-muted-foreground"}`}>
                                          {child.status}
                                        </span>
                                        <div className="flex gap-1 shrink-0">
                                          <Button size="sm" variant="ghost" onClick={() => setEditItem(child)}>Edit</Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                            onClick={() => setDeleteTarget(child)}
                                          >
                                            Delete
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </TableCell>
                            </TableRow>
                          </CollapsibleContent>
                        )}
                      </>
                    </Collapsible>
                  );
                })
              )}
            </TableBody>
          </table>
          {!isLoading && totalItems > 0 ? (
            <ListPagination
              total={totalItems}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
              noun={["item", "items"]}
            />
          ) : null}
        </CardContent>
      </Card>

      {/* ItemDialog is kept mounted at all times — Radix Dialog
          manages open/close internally (DialogContent is portaled
          in/out on the open prop). Earlier attempt at conditional
          mount via `{isDialogOpen && ...}` interfered with Radix's
          close-animation cleanup and left the dialog visually stuck
          on the X click. The mount cost is now amortised by
          startTransition wrapping the open state update in
          inventory-operation.tsx. */}
      <ItemDialog
        key={editItem ? `edit-${editItem.id}` : "create"}
        open={isDialogOpen}
        onClose={handleClose}
        editItem={editItem}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the item from inventory. If pricing rows or plan
              entitlements still reference it, the delete will be blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteItemMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              disabled={deleteItemMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteItemMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AuditHistoryDialog
        open={historyTarget !== null}
        onOpenChange={(o) => !o && setHistoryTarget(null)}
        tableName="inventory_items"
        recordId={historyTarget?.id ?? null}
        recordLabel={historyTarget ? `${historyTarget.itemCode ?? `#${historyTarget.id}`} · ${historyTarget.name}` : undefined}
      />

      <ImportItemsDialog open={importOpen} onOpenChange={setImportOpen} />

      {/* Bulk: Delete items confirmation */}
      <AlertDialog open={bulkConfirm === "delete"} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} items?</AlertDialogTitle>
            <AlertDialogDescription>
              Items with vendor pricing or plan entitlements attached will be skipped.
              A summary toast will report how many succeeded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDeleteItems}
              disabled={bulkBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkBusy ? "Deleting…" : `Delete ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk: Mark Discontinued confirmation */}
      <AlertDialog open={bulkConfirm === "discontinue"} onOpenChange={(o) => !o && setBulkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {selectedIds.size} items as Discontinued?</AlertDialogTitle>
            <AlertDialogDescription>
              Sets status = Discontinued. Items stay in the catalog and existing stock /
              charges are unaffected, but they no longer surface in active dropdowns.
              Reversible from the Edit dialog.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDiscontinue} disabled={bulkBusy}>
              {bulkBusy ? "Updating…" : `Mark ${selectedIds.size} discontinued`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk: Move to Category */}
      <Dialog open={bulkMoveOpen} onOpenChange={(o) => !o && setBulkMoveOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move {selectedIds.size} items to a different category</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Select
              value={bulkMoveCategoryId?.toString() ?? ""}
              onValueChange={(v) => setBulkMoveCategoryId(Number(v))}
            >
              <SelectTrigger><SelectValue placeholder="Pick a category…" /></SelectTrigger>
              <SelectContent>
                {categories?.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id.toString()}>
                    {cat.name} | {cat.chargingModel.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              All selected items will be reassigned to this category. Useful for emptying
              a category before deleting it.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkMoveOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button onClick={handleBulkMoveCategory} disabled={bulkBusy || bulkMoveCategoryId == null}>
              {bulkBusy ? "Moving…" : `Move ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk: Change Usage Model */}
      <Dialog open={bulkUsageModelOpen} onOpenChange={(o) => !o && setBulkUsageModelOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change usage model on {selectedIds.size} items</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Select
              value={bulkUsageModel ?? ""}
              onValueChange={(v) => setBulkUsageModel(v as keyof typeof ChargingModel)}
            >
              <SelectTrigger><SelectValue placeholder="Pick a usage model…" /></SelectTrigger>
              <SelectContent>
                {Object.values(ChargingModel).map((m) => (
                  <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              All selected items will be re-tagged. Items currently configured for the model
              you're moving away from (e.g. Pay Per Use with a Selling Price set) may need
              follow-up edits — the change here only updates the chargingModel column.
              For CWL items, ensure plan-entitlement matrices exist before switching.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUsageModelOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button onClick={handleBulkChangeUsageModel} disabled={bulkBusy || !bulkUsageModel}>
              {bulkBusy ? "Updating…" : `Apply to ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
